/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { jsPDF } from 'jspdf';
import { Folio, GoodsItem, PoRecord, PoFolioSnapshot } from '../types.ts';
import { CORPORATE_LOGO_BASE64 } from './corporateLogoBase64.ts';

export const CORPORATE_LOGO_URL = 'https://www.grupoisn.com.mx/wp-content/uploads/2025/12/cropped-WhatsApp-Image-2025-12-19-at-12.31.53.jpeg';

/**
 * Loads the corporate logo as base64 data URL for embedding in jsPDF documents.
 * Uses the embedded high-resolution base64 asset to guarantee 100% reliability across all PDF generations.
 */
export async function getCorporateLogoBase64(): Promise<string> {
  return CORPORATE_LOGO_BASE64;
}

/**
 * Draws the official SNF corporate header and logo on a jsPDF document.
 */
function drawCorporateHeader(
  doc: jsPDF,
  docTypeTitle: string,
  docSubtitle: string,
  badgeText: string,
  accentColor: [number, number, number] = [188, 17, 0], // Brand Red #BC1100
  logoImgData: string | HTMLImageElement | null = CORPORATE_LOGO_BASE64
) {
  const pageWidth = 216; // Letter width in mm

  // 1. Top Accent Bar (Brand Red)
  doc.setFillColor(...accentColor);
  doc.rect(0, 0, pageWidth, 3.5, 'F');

  // 2. Main Header Container (Slate-900 / #0F172A)
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 3.5, pageWidth, 31, 'F');

  // 3. Draw Corporate Emblem / Logo Mark
  let textStartX = 42;
  if (logoImgData) {
    try {
      // White contrast base plate for crisp logo presentation
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(12, 6.5, 26, 25, 2, 2, 'F');
      doc.addImage(logoImgData as any, 'JPEG', 13, 7.5, 24, 23);
      textStartX = 42;
    } catch {
      drawFallbackEmblem();
    }
  } else {
    drawFallbackEmblem();
  }

  function drawFallbackEmblem() {
    doc.setFillColor(...accentColor);
    doc.roundedRect(12, 7.5, 22, 23, 2, 2, 'F');
    doc.setFillColor(225, 29, 72);
    doc.roundedRect(14, 10.5, 18, 17, 1.5, 1.5, 'F');
    doc.setFillColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('SNF', 16, 21.5);
    textStartX = 38;
  }

  // Company Name Logotype
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('SNF', textStartX, 15);

  // Corporate Subtitle
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(203, 213, 225); // Slate-300
  doc.text('Servicios Nuevos De Filtración Hispanomexicános', textStartX, 20);

  // Document Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(239, 68, 68); // Red-500
  doc.text(docTypeTitle.toUpperCase().substring(0, 58), textStartX, 26);

  // 4. Document Badge Right Box
  doc.setFillColor(30, 41, 59); // Slate-800
  doc.setDrawColor(...accentColor);
  doc.setLineWidth(0.6);
  doc.roundedRect(144, 7, 58, 24, 2, 2, 'FD');

  doc.setTextColor(148, 163, 184); // Slate-400
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6);
  doc.text(docSubtitle.toUpperCase().substring(0, 34), 147, 12);

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  const badgeLines = doc.splitTextToSize(badgeText, 52);
  doc.text(badgeLines, 147, 19.5);
}

/**
 * Draws the official footer with legal disclaimer, audit trail timestamp, and signature lines.
 */
function drawCorporateFooter(
  doc: jsPDF,
  createdBy: string,
  includeSignatures: boolean = true
) {
  const pageHeight = doc.internal.pageSize.getHeight() || 279.4;

  if (includeSignatures) {
    const sigY = pageHeight - 48;
    // Signature Boxes
    doc.setDrawColor(203, 213, 225); // Slate-300
    doc.setLineWidth(0.4);

    // Box 1: Driver / Dispatcher
    doc.line(20, sigY + 12, 90, sigY + 12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(51, 65, 85);
    doc.text('FIRMA ENTREGADO / CHOFER OPERADOR', 25, sigY + 16);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text('SNF - Traslados & Logística', 29, sigY + 20);

    // Box 2: Client / Plant Reception (GR)
    doc.line(126, sigY + 12, 196, sigY + 12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(51, 65, 85);
    doc.text('FIRMA RECIBIDO CONFORME (GR) / PLANTA', 128, sigY + 16);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text('Socio Comercial - Confirmación de Recepción', 129, sigY + 20);
  }

  // Divider Line at bottom of page
  const footerLineY = pageHeight - 16;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.3);
  doc.line(14, footerLineY, 202, footerLineY);

  const textY = footerLineY + 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(148, 163, 184);
  doc.text('Este documento es una representación impresa oficial generada por el Sistema Integral de Control Logístico de SNF.', 14, textY);
  doc.text('La autenticidad de los estados de trazabilidad y la firma electrónica de recepción puede validarse escaneando el código QR oficial.', 14, textY + 3.5);
  doc.text(`Fecha de Emisión: ${new Date().toLocaleString('es-MX')} | Emisor: ${createdBy || 'Sistema de Control SNF'} | Hash: SHA256-GR-${Math.random().toString(36).substring(2, 10).toUpperCase()}`, 14, textY + 7);
}

/**
 * Generates and downloads the official Folio Remisión de Embarque PDF.
 */
export async function downloadFolioPDF(
  folio: Folio,
  options?: {
    hidePrices?: boolean;
    poNumber?: string;
    isPoIntegrated?: boolean;
  }
) {
  const hidePrices = Boolean(options?.hidePrices);
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const logoBase64 = await getCorporateLogoBase64();
  const trackingUrl = `${window.location.origin}${window.location.pathname}?track=${folio.id}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(trackingUrl)}`;

  const img = new Image();
  img.crossOrigin = 'Anonymous';
  img.src = qrUrl;

  const renderContent = (qrImg: HTMLImageElement | null) => {
    // 1. Header with Logo
    drawCorporateHeader(
      doc,
      'REMISIÓN DE EMBARQUE Y CERTIFICADO DE TRAZABILIDAD',
      'DOCUMENTO OFICIAL DE CONTROL',
      `FOLIO: ${folio.id}`,
      [188, 17, 0],
      logoBase64
    );

    let y = 39;

    // 2. Info Grid Box (Left side: width 138mm)
    doc.setDrawColor(226, 232, 240);
    doc.setFillColor(248, 250, 252);
    doc.setLineWidth(0.4);
    doc.roundedRect(14, y, 138, 48, 3, 3, 'FD');

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('DATOS GENERALES DE LA OPERACIÓN', 18, y + 6);

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);

    // Row 1: Cliente & Orden de Compra
    doc.text('Cliente / Socio:', 18, y + 13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    const clientStr = (folio.clientName || 'General').substring(0, 22);
    doc.text(clientStr, 44, y + 13);

    // Orden de Compra (PO): Si está integrado, muestra el nombre de la PO en negritas. Si no, muestra "NO INTEGRADA PO" en negritas.
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Orden de Compra:', 84, y + 13);

    const rawPo = (options?.poNumber !== undefined ? options.poNumber : folio.poNumber) || '';
    const isPoIntegrated = Boolean(
      rawPo &&
      rawPo.trim() !== '' &&
      rawPo.toUpperCase() !== 'SIN PO' &&
      rawPo.toUpperCase() !== 'S/PO' &&
      rawPo.toUpperCase() !== 'NO INTEGRADA PO' &&
      rawPo.toUpperCase() !== 'NO INTEGRADA' &&
      rawPo.toUpperCase() !== 'SIN_PO'
    );

    const poDisplayStr = isPoIntegrated ? rawPo.trim() : 'NO INTEGRADA PO';

    // REMARCADO EN NEGRITAS (BOLD)
    doc.setFont('helvetica', 'bold');
    if (isPoIntegrated) {
      doc.setTextColor(15, 23, 42); // Slate-900 para PO asignada
    } else {
      doc.setTextColor(220, 38, 38); // Rojo corporativo destacado para NO INTEGRADA PO
    }

    let boundedPoStr = poDisplayStr;
    const maxPoW = 38; // mm
    if (doc.getTextWidth(boundedPoStr) > maxPoW) {
      while (doc.getTextWidth(boundedPoStr + '...') > maxPoW && boundedPoStr.length > 5) {
        boundedPoStr = boundedPoStr.substring(0, boundedPoStr.length - 1);
      }
      boundedPoStr += '...';
    }
    doc.text(boundedPoStr, 114, y + 13);

    // Row 2: Centro de Costos & Fecha
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Centro Costos:', 18, y + 19);
    const ccStr = (folio.costCenterName && !folio.costCenterName.includes('Planta Central') ? folio.costCenterName : 'Sede Principal').substring(0, 22);
    doc.text(ccStr, 44, y + 19);

    doc.text('Fecha y Hora:', 84, y + 19);
    const dateStr = `${new Date(folio.createdDate).toLocaleDateString('es-MX')} ${new Date(folio.createdDate).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`;
    doc.text(dateStr, 106, y + 19);

    // Row 3: Chofer & Estatus
    doc.text('Chofer Asignado:', 18, y + 25);
    const driverStr = `${folio.driverName || 'En Ruta'} (Ec: ${folio.economicNumber || 'N/A'})`.substring(0, 22);
    doc.text(driverStr, 44, y + 25);

    doc.text('Estatus Folio:', 84, y + 25);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38);
    doc.text(`${folio.status.replace(/_/g, ' ').substring(0, 16)}`, 106, y + 25);

    // Row 4: Ruta Origen & Destino (bounded text with strict box containment to never collide with QR)
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Ruta / Trayecto:', 18, y + 31);
    
    // Strict bounding: Left box ends at X=152. Text starts at X=44. Max allowed width = 104mm
    const rawOrigin = (folio.origin || 'Planta Cliente').trim();
    const rawDest = (folio.destination || 'CEDIS / PLANTA').trim();
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(51, 65, 85);
    
    let routeText = `Origen: ${rawOrigin} -> Destino: ${rawDest}`;
    const maxRouteW = 104; // mm
    if (doc.getTextWidth(routeText) > maxRouteW) {
      while (doc.getTextWidth(routeText + '...') > maxRouteW && routeText.length > 15) {
        routeText = routeText.substring(0, routeText.length - 2);
      }
      routeText = routeText + '...';
    }
    doc.text(routeText, 44, y + 31);

    // Row 5: Proceso Asignado
    let processLabel = 'DECAPADO TÉRMICO';
    let processFlow = 'Decapado -> Lavado -> Inspección';
    if (folio.processType === 'DECAPADO_QUIMICO') {
      processLabel = 'DECAPADO QUÍMICO';
      processFlow = 'Decapado -> Lavado -> Inspección';
    } else if (folio.processType === 'DECAPADO_GRANALLADORA' || folio.hasGranallado) {
      processLabel = 'DECAPADO POR GRANALLADORA';
      processFlow = 'Decapado -> Inspección (Directo)';
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(71, 85, 105);
    doc.text('Proceso Asignado:', 18, y + 37);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(188, 17, 0);
    
    let processFullStr = `${processLabel} (${processFlow})`;
    if (doc.getTextWidth(processFullStr) > 104) {
      while (doc.getTextWidth(processFullStr + '...') > 104 && processFullStr.length > 15) {
        processFullStr = processFullStr.substring(0, processFullStr.length - 2);
      }
      processFullStr = processFullStr + '...';
    }
    doc.text(processFullStr, 44, y + 37);

    // 3. QR Code Panel (Right side: X=156, width=46mm, strictly isolated)
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(156, y, 46, 48, 3, 3, 'FD');

    if (qrImg) {
      try {
        doc.addImage(qrImg, 'PNG', 161, y + 3, 36, 36);
      } catch (e) {
        console.warn('QR image draw error', e);
      }
    }

    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text('VALIDAR TRAZABILIDAD QR', 159, y + 43);

    y += 53;

    // 4. Material Items Table Header (X=14, Width=188)
    doc.setFillColor(15, 23, 42); // Slate-900 table header
    doc.rect(14, y, 188, 8, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);

    if (hidePrices) {
      doc.text('CÓDIGO SKU / PARTE', 18, y + 5.5);
      doc.text('DESCRIPCIÓN Y ESPECIFICACIÓN DEL MATERIAL', 55, y + 5.5);
      doc.text('CANTIDAD EMBARCADA', 155, y + 5.5);
    } else {
      doc.text('CÓDIGO SKU', 18, y + 5.5);
      doc.text('DESCRIPCIÓN Y ESPECIFICACIÓN DEL MATERIAL', 52, y + 5.5);
      doc.text('CANTIDAD', 136, y + 5.5);
      doc.text('P.U. ESTIMADO', 156, y + 5.5);
      doc.text('IMPORTE MXN', 180, y + 5.5);
    }

    y += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(51, 65, 85);

    let totalQuantity = 0;
    let totalEstimatedAmount = 0;

    if (folio.items && folio.items.length > 0) {
      folio.items.forEach((it, idx) => {
        const qty = Number(it.quantity) || 1;
        const price = Number(it.unitPriceMXN) || 0;
        const rowTotal = Number(it.totalMXN) || (qty * price) || 0;

        totalQuantity += qty;
        totalEstimatedAmount += rowTotal;

        // Clean SKU formatting (never allow ultra long string to smash into description)
        let skuCode = String(it.goodsItemId || (it as { code?: string }).code || `SKU-${idx + 1}`);
        if (skuCode.length > 15) {
          skuCode = skuCode.substring(0, 13) + '...';
        }

        const descText = String(it.description || 'Tratamiento y Decapado de Pieza');

        if (hidePrices) {
          const descLines = doc.splitTextToSize(descText, 95);
          const rowHeight = Math.max(7.5, descLines.length * 4.2 + 3);

          if (idx % 2 === 1) {
            doc.setFillColor(248, 250, 252);
            doc.rect(14, y, 188, rowHeight, 'F');
          }

          // SKU Code
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(71, 85, 105);
          doc.text(skuCode, 18, y + 4.8);

          // Description
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(15, 23, 42);
          doc.text(descLines, 55, y + 4.8);

          // Quantity
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(15, 23, 42);
          doc.text(`${qty} PZA`, 155, y + 4.8);

          y += rowHeight;
        } else {
          const descLines = doc.splitTextToSize(descText, 80);
          const rowHeight = Math.max(7.5, descLines.length * 4.2 + 3);

          if (idx % 2 === 1) {
            doc.setFillColor(248, 250, 252);
            doc.rect(14, y, 188, rowHeight, 'F');
          }

          // SKU Code
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(71, 85, 105);
          doc.text(skuCode, 18, y + 4.8);

          // Description
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(15, 23, 42);
          doc.text(descLines, 52, y + 4.8);

          // Quantity
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(51, 65, 85);
          doc.text(`${qty} PZA`, 136, y + 4.8);

          // Unit Price
          doc.setFont('helvetica', 'normal');
          doc.text(`${price.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 156, y + 4.8);

          // Line Total
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(15, 23, 42);
          doc.text(`${rowTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 180, y + 4.8);

          y += rowHeight;
        }
      });
    } else {
      doc.setFillColor(248, 250, 252);
      doc.rect(14, y, 188, 8, 'F');
      doc.text('SKU-001', 18, y + 5);
      doc.text('Servicio Especializado de Decapado y Limpieza de Racks', 52, y + 5);
      if (hidePrices) {
        doc.text('1 LOTE', 155, y + 5);
      } else {
        doc.text('1 LOTE', 136, y + 5);
        doc.text('$25,000.00', 156, y + 5);
        doc.text('$25,000.00', 180, y + 5);
      }
      totalQuantity = 1;
      totalEstimatedAmount = 25000;
      y += 8;
    }

    // Table Summary Line
    doc.setFillColor(241, 245, 249);
    doc.rect(14, y, 188, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);

    if (hidePrices) {
      doc.text('TOTAL DE PIEZAS / ITEMS EMBARCADOS:', 75, y + 5.5);
      doc.text(`${totalQuantity} PIEZAS`, 155, y + 5.5);
    } else {
      doc.text('TOTALES:', 100, y + 5.5);
      doc.text(`${totalQuantity} PIEZAS`, 136, y + 5.5);
      doc.text(`${totalEstimatedAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 172, y + 5.5);
    }

    y += 13;

    // 5. Notes & Observations Block
    if (folio.notes) {
      doc.setDrawColor(226, 232, 240);
      doc.setFillColor(254, 252, 232); // Amber-50 light
      doc.roundedRect(14, y, 188, 16, 2, 2, 'FD');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(180, 83, 9); // Amber-700
      doc.text('OBSERVACIONES Y NOTAS DE RECEPCIÓN EN PLANTA:', 18, y + 5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(71, 85, 105);
      const splitNotes = doc.splitTextToSize(folio.notes, 180);
      doc.text(splitNotes, 18, y + 10);

      y += 20;
    }

    // 6. Signatures and Corporate Footer
    drawCorporateFooter(doc, folio.createdBy || 'Sistema de Control Logístico SNF', true);

    // Save File
    doc.save(`Remision_Embarque_${folio.id}.pdf`);
  };

  img.onload = () => renderContent(img);
  img.onerror = () => renderContent(null);
}

/**
 * Generates and downloads the official Prefacturación PDF Report.
 */
export async function downloadPreInvoicePDF(invoice: {
  id: string;
  folioId: string;
  clientName: string;
  issueDate: string;
  subtotalMXN: number;
  taxMXN: number;
  totalMXN: number;
  status: string;
  items: { description: string; quantity: number; priceMXN: number | string; totalMXN: number | string }[];
}) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const logoBase64 = await getCorporateLogoBase64();

  // 1. Corporate Header with Logo
  drawCorporateHeader(
    doc,
    'REPORTE OFICIAL DE PREFACTURACIÓN DE FLETES Y SERVICIOS',
    'CONCILIACIÓN FINANCIERA',
    `NO. ${invoice.id}`,
    [188, 17, 0], // Brand Red
    logoBase64
  );

  let y = 38;

  // 2. Client & Billing Info Box
  doc.setDrawColor(226, 232, 240);
  doc.setFillColor(248, 250, 252);
  doc.setLineWidth(0.4);
  doc.roundedRect(14, y, 188, 30, 3, 3, 'FD');

  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text('DATOS FISCALES Y RECEPTOR DE LA PREFACTURA', 18, y + 6);

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);

  doc.text('Receptor / Cliente:', 18, y + 13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(`${invoice.clientName}`, 52, y + 13);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text('Referencia Folio GR:', 18, y + 19);
  doc.text(`${invoice.folioId}`, 52, y + 19);

  doc.text('Fecha de Emisión:', 18, y + 25);
  doc.text(`${invoice.issueDate}`, 52, y + 25);

  doc.text('Estado de Conciliación:', 120, y + 13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(16, 185, 129); // Emerald-500
  doc.text(`${invoice.status.toUpperCase()} (GR FACTURADO)`, 156, y + 13);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text('Moneda Operativa:', 120, y + 19);
  doc.text('MXN (Pesos Mexicanos)', 156, y + 19);

  y += 36;

  // 3. Items Table Header
  doc.setFillColor(15, 23, 42); // Slate-900 table header
  doc.rect(14, y, 188, 7.5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255);
  doc.text('CONCEPTO / DESCRIPCIÓN DEL SERVICIO O FLETE', 18, y + 5);
  doc.text('CANTIDAD', 125, y + 5);
  doc.text('P. UNITARIO', 150, y + 5);
  doc.text('IMPORTE MXN', 176, y + 5);

  y += 7.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(51, 65, 85);

  if (invoice.items && invoice.items.length > 0) {
    invoice.items.forEach((it, idx) => {
      if (idx % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(14, y, 188, 7, 'F');
      }

      const qty = Number(it.quantity) || 1;
      const price = Number(it.priceMXN) || 0;
      const total = Number(it.totalMXN) || (qty * price) || 0;

      doc.text(String(it.description || 'Servicio de Flete y Decapado').substring(0, 58), 18, y + 4.8);
      doc.text(`${qty}`, 125, y + 4.8);
      doc.text(`${price.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 150, y + 4.8);
      doc.text(`${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 176, y + 4.8);

      y += 7;
    });
  }

  y += 6;

  // 4. Financial Summary Card
  const subtotal = Number(invoice.subtotalMXN) || (invoice.totalMXN / 1.16);
  const tax = Number(invoice.taxMXN) || (invoice.totalMXN - subtotal);
  const total = Number(invoice.totalMXN) || 0;

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(120, y, 82, 28, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(71, 85, 105);

  doc.text('SUBTOTAL:', 125, y + 7);
  doc.text(`${subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`, 162, y + 7);

  doc.text('IVA (16%):', 125, y + 13);
  doc.text(`${tax.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`, 162, y + 13);

  doc.setFillColor(188, 17, 0); // Highlight Red Total Box
  doc.rect(120, y + 18, 82, 10, 'F');

  doc.setFontSize(9.5);
  doc.setTextColor(255, 255, 255);
  doc.text('TOTAL GENERAL:', 125, y + 25);
  doc.text(`${total.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`, 158, y + 25);

  y += 38;

  // 5. Signatures and Corporate Footer
  drawCorporateFooter(doc, 'Departamento de Facturación SNF', true);

  // Save File
  doc.save(`Prefactura_${invoice.id}.pdf`);
}

/**
 * Generates and downloads the official Goods Catalog PDF.
 */
export async function downloadGoodsCatalogPDF(
  items: GoodsItem[], 
  clientName: string,
  options?: { hidePrices?: boolean }
) {
  const hidePrices = Boolean(options?.hidePrices);
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const logoBase64 = await getCorporateLogoBase64();

  drawCorporateHeader(
    doc,
    'CATÁLOGO TÉCNICO OFICIAL DE PIEZAS Y MATERIALES',
    'ESPECIFICACIONES CLIENTE',
    `CLIENTE: ${clientName.substring(0, 15)}`,
    [188, 17, 0],
    logoBase64
  );

  let y = 38;

  doc.setFillColor(15, 23, 42);
  doc.rect(14, y, 188, 7.5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255);

  if (hidePrices) {
    doc.text('CÓDIGO SKU / PARTE', 18, y + 5);
    doc.text('DESCRIPCIÓN Y ESPECIFICACIÓN DEL MATERIAL', 60, y + 5);
    doc.text('UNIDAD DE MEDIDA', 160, y + 5);
  } else {
    doc.text('CÓDIGO SKU', 18, y + 5);
    doc.text('DESCRIPCIÓN DEL MATERIAL', 52, y + 5);
    doc.text('UNIDAD', 135, y + 5);
    doc.text('PRECIO MXN', 158, y + 5);
    doc.text('PRECIO USD', 182, y + 5);
  }

  y += 7.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(51, 65, 85);

  items.forEach((it, idx) => {
    if (idx % 2 === 1) {
      doc.setFillColor(248, 250, 252);
      doc.rect(14, y, 188, 7, 'F');
    }

    if (hidePrices) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(71, 85, 105);
      doc.text(String(it.code || `SKU-${idx + 1}`), 18, y + 4.8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      doc.text(String(it.description || 'Material Decapado').substring(0, 65), 60, y + 4.8);
      doc.setFont('helvetica', 'bold');
      doc.text(String(it.unit || 'PZA').toUpperCase(), 160, y + 4.8);
    } else {
      doc.text(String(it.code || `SKU-${idx + 1}`), 18, y + 4.8);
      doc.text(String(it.description || 'Material Decapado').substring(0, 48), 52, y + 4.8);
      doc.text(String(it.unit || 'PZA'), 135, y + 4.8);
      doc.text(`${Number(it.priceMXN || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 158, y + 4.8);
      doc.text(`${Number(it.priceUSD || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 182, y + 4.8);
    }

    y += 7;

    // Handle multi-page if catalog is long
    if (y > 240 && idx < items.length - 1) {
      drawCorporateFooter(doc, 'Catálogo Técnico SNF', false);
      doc.addPage();
      drawCorporateHeader(
        doc,
        'CATÁLOGO TÉCNICO OFICIAL DE PIEZAS Y MATERIALES',
        'ESPECIFICACIONES CLIENTE',
        `CLIENTE: ${clientName.substring(0, 15)}`,
        [188, 17, 0],
        logoBase64
      );
      y = 38;

      doc.setFillColor(15, 23, 42);
      doc.rect(14, y, 188, 7.5, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(255, 255, 255);

      if (hidePrices) {
        doc.text('CÓDIGO SKU / PARTE', 18, y + 5);
        doc.text('DESCRIPCIÓN Y ESPECIFICACIÓN DEL MATERIAL', 60, y + 5);
        doc.text('UNIDAD DE MEDIDA', 160, y + 5);
      } else {
        doc.text('CÓDIGO SKU', 18, y + 5);
        doc.text('DESCRIPCIÓN DEL MATERIAL', 52, y + 5);
        doc.text('UNIDAD', 135, y + 5);
        doc.text('PRECIO MXN', 158, y + 5);
        doc.text('PRECIO USD', 182, y + 5);
      }
      y += 7.5;
    }
  });

  drawCorporateFooter(doc, 'Catálogo Técnico SNF', false);
  doc.save(`Catalogo_Tecnico_${clientName.replace(/\s+/g, '_')}.pdf`);
}

/**
 * Generates and downloads the official PO History & GR Confirmation Financial Report PDF.
 */
export async function downloadPoHistoryReportPDF(params: {
  clientName: string;
  poNumber: string;
  annualBudgetMXN: number;
  previousBalanceMXN: number;
  totalDeliveredMXN: number;
  availableBalanceMXN: number;
  dateFilterLabel: string;
  poFilterLabel?: string;
  folios: Array<Folio & { resolvedPoNumber?: string; resolvedPoStatus?: string }>;
  hidePrices?: boolean;
}) {
  const {
    clientName,
    poNumber,
    annualBudgetMXN,
    previousBalanceMXN,
    totalDeliveredMXN,
    availableBalanceMXN,
    dateFilterLabel,
    poFilterLabel,
    folios,
    hidePrices = false
  } = params;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const logoBase64 = await getCorporateLogoBase64();

  drawCorporateHeader(
    doc,
    hidePrices ? 'REPORTE OPERATIVO Y AUDITORÍA DE ÓRDENES DE COMPRA (PO)' : 'REPORTE FINANCIERO Y AUDITORÍA DE ÓRDENES DE COMPRA (PO)',
    hidePrices ? `CONTROL DE ENTREGAS Y PIEZAS • ${poNumber || 'PO-ANUAL'}` : `SISTEMA FINANCIERO DE CONTROL • ${poNumber || 'PO-ANUAL'}`,
    `CLIENTE: ${clientName.substring(0, 15)}`,
    [188, 17, 0],
    logoBase64
  );

  let y = 38;

  // Banner Block
  doc.setFillColor(248, 250, 252); // Slate-50
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(14, y, 188, 30, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(15, 23, 42);

  if (hidePrices) {
    doc.text('RESUMEN DE MÉTRICAS OPERATIVAS DE ORDEN DE COMPRA (CONTROL DE ENTREGAS)', 18, y + 6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(51, 65, 85);
    doc.text(`PO - ${poNumber || 'VIGENTE'}: Registro Operativo Oficial`, 18, y + 12);
    doc.text(`Total Folios Asignados: ${folios.length} Folios`, 122, y + 12);

    const totalQtyAll = folios.reduce((sum, f) => sum + (f.items?.reduce((s, it) => s + (Number(it.quantity) || 0), 0) || 0), 0);
    doc.text(`Total Piezas Registradas: ${totalQtyAll.toLocaleString()} PZAS`, 18, y + 18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(16, 185, 129); // Emerald-600
    doc.text(`Control de Trazabilidad: 100% OPERATIVO`, 122, y + 18);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    const filterSummary = poFilterLabel ? `Filtro PO: ${poFilterLabel} | Período: ${dateFilterLabel}` : `Período Consultado: ${dateFilterLabel}`;
    doc.text(`${filterSummary} | Auditoría de Materiales y Cantidades`, 18, y + 25);
  } else {
    doc.text('RESUMEN DE MÉTRICAS FINANCIERAS (CONTROLLER DE OC/PO)', 18, y + 6);

    // Line 1: Presupuesto & Saldo
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(51, 65, 85);
    doc.text(`PO - ${poNumber || 'VIGENTE'}: ${annualBudgetMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 18, y + 12);
    doc.text(`Saldo a Favor Anterior: ${previousBalanceMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 122, y + 12);

    // Line 2: Entregado GR & Disponible
    doc.text(`Valor Total Entregado (GR): ${totalDeliveredMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 18, y + 18);
    
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(16, 185, 129); // Emerald-600
    doc.text(`Presupuesto Disponible: ${availableBalanceMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 122, y + 18);

    const totalCapacity = annualBudgetMXN + previousBalanceMXN;
    const percentUsed = totalCapacity > 0 
      ? ((totalDeliveredMXN / totalCapacity) * 100).toFixed(1)
      : '0.0';

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    const filterSummary = poFilterLabel ? `Filtro PO: ${poFilterLabel} | Período: ${dateFilterLabel}` : `Período Consultado: ${dateFilterLabel}`;
    doc.text(`${filterSummary} | % Consumido: ${percentUsed}%`, 18, y + 25);
  }

  y += 35;

  // Table Header
  doc.setFillColor(15, 23, 42);
  doc.rect(14, y, 188, 7.5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.8);
  doc.setTextColor(255, 255, 255);
  if (hidePrices) {
    doc.text('FOLIO', 16, y + 5);
    doc.text('FECHA', 38, y + 5);
    doc.text('CENTRO COSTOS', 62, y + 5);
    doc.text('ORDEN COMPRA (PO)', 102, y + 5);
    doc.text('ESTATUS ENTREGA', 140, y + 5);
    doc.text('TOTAL PIEZAS', 170, y + 5);
  } else {
    doc.text('FOLIO', 16, y + 5);
    doc.text('FECHA', 36, y + 5);
    doc.text('CENTRO COSTOS', 56, y + 5);
    doc.text('ORDEN COMPRA (PO)', 88, y + 5);
    doc.text('ESTATUS GR', 120, y + 5);
    doc.text('PIEZAS', 150, y + 5);
    doc.text('VALOR MXN', 170, y + 5);
  }

  y += 7.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.8);
  doc.setTextColor(51, 65, 85);

  let pageFoliosCount = 0;
  let pagePieces = 0;
  let pageMXN = 0;
  let globalFoliosCount = 0;
  let globalPieces = 0;
  let globalMXN = 0;

  folios.forEach((folio, idx) => {
    if (idx % 2 === 1) {
      doc.setFillColor(248, 250, 252);
      doc.rect(14, y, 188, 6.5, 'F');
    }

    const totalVal = folio.items.reduce((acc, it) => acc + (Number(it.totalMXN) || 0), 0);
    const totalQty = folio.items.reduce((acc, it) => acc + (Number(it.quantity) || 0), 0);
    const dateStr = (folio.shipmentDate || folio.createdDate || '').substring(0, 10);
    const isGrConfirmed = folio.grStatus === 'CONFIRMADO';
    const rawPo = folio.resolvedPoNumber || folio.poNumber || '';
    const isSinPo = Boolean(
      folio.resolvedPoStatus === 'SIN_PO' ||
      !rawPo ||
      rawPo.trim() === '' ||
      rawPo.toUpperCase() === 'SIN PO' ||
      rawPo.toUpperCase() === 'S/PO' ||
      rawPo.toUpperCase() === 'NO INTEGRADA PO' ||
      rawPo.toUpperCase() === 'NO INTEGRADA' ||
      rawPo.toUpperCase() === 'SIN_PO'
    );
    const assignedPo = isSinPo ? 'NO INTEGRADA PO' : rawPo.trim().substring(0, 18);

    pageFoliosCount++;
    pagePieces += totalQty;
    pageMXN += totalVal;

    globalFoliosCount++;
    globalPieces += totalQty;
    globalMXN += totalVal;

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 65, 85);
    doc.text(String(folio.id), 16, y + 4.5);
    
    doc.setFont('helvetica', 'normal');
    if (hidePrices) {
      doc.text(dateStr, 38, y + 4.5);
      doc.text(String(folio.costCenterName || 'N/A').substring(0, 20), 62, y + 4.5);

      doc.setFont('helvetica', 'bold');
      if (isSinPo) {
        doc.setTextColor(220, 38, 38);
        doc.text('NO INTEGRADA PO', 102, y + 4.5);
      } else {
        doc.setTextColor(79, 70, 229);
        doc.text(assignedPo, 102, y + 4.5);
      }

      if (isGrConfirmed) {
        doc.setTextColor(5, 150, 105);
        doc.text('ENTREGADO', 140, y + 4.5);
      } else {
        doc.setTextColor(217, 119, 6);
        doc.text('EN PROCESO', 140, y + 4.5);
      }

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(`${totalQty} PZAS`, 170, y + 4.5);
    } else {
      doc.text(dateStr, 36, y + 4.5);
      doc.text(String(folio.costCenterName || 'N/A').substring(0, 18), 56, y + 4.5);

      doc.setFont('helvetica', 'bold');
      if (isSinPo) {
        doc.setTextColor(220, 38, 38);
        doc.text('NO INTEGRADA PO', 88, y + 4.5);
      } else {
        doc.setTextColor(79, 70, 229);
        doc.text(assignedPo, 88, y + 4.5);
      }

      if (isGrConfirmed) {
        doc.setTextColor(5, 150, 105);
        doc.text('GR FACTURADO', 120, y + 4.5);
      } else {
        doc.setTextColor(217, 119, 6);
        doc.text('GR POR FACTURAR', 120, y + 4.5);
      }

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(51, 65, 85);
      doc.text(String(totalQty), 150, y + 4.5);
      doc.setFont('helvetica', 'bold');
      doc.text(`${totalVal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 170, y + 4.5);
    }

    y += 6.5;

    // Multi-page check
    if (y > 228 && idx < folios.length - 1) {
      doc.setFillColor(241, 245, 249);
      doc.setDrawColor(203, 213, 225);
      doc.rect(14, y, 188, 6.5, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.8);
      doc.setTextColor(15, 23, 42);
      doc.text(`SUBTOTAL PÁGINA (${pageFoliosCount} FOLIOS)`, 16, y + 4.5);
      if (hidePrices) {
        doc.text(`${pagePieces.toLocaleString()} PZAS`, 170, y + 4.5);
      } else {
        doc.text(`${pagePieces.toLocaleString()} PZAS`, 150, y + 4.5);
        doc.text(`$${pageMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 170, y + 4.5);
      }

      doc.addPage();
      drawCorporateHeader(
        doc,
        hidePrices ? 'REPORTE OPERATIVO Y AUDITORÍA DE ÓRDENES DE COMPRA (PO)' : 'REPORTE FINANCIERO Y AUDITORÍA DE ÓRDENES DE COMPRA (PO)',
        hidePrices ? `CONTROL DE ENTREGAS Y PIEZAS • ${poNumber || 'PO-ANUAL'}` : `SISTEMA FINANCIERO DE CONTROL • ${poNumber || 'PO-ANUAL'}`,
        `CLIENTE: ${clientName.substring(0, 15)}`,
        [188, 17, 0],
        logoBase64
      );
      y = 38;

      doc.setFillColor(15, 23, 42);
      doc.rect(14, y, 188, 7.5, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.8);
      doc.setTextColor(255, 255, 255);
      if (hidePrices) {
        doc.text('FOLIO', 16, y + 5);
        doc.text('FECHA', 38, y + 5);
        doc.text('CENTRO COSTOS', 62, y + 5);
        doc.text('ORDEN COMPRA (PO)', 102, y + 5);
        doc.text('ESTATUS ENTREGA', 140, y + 5);
        doc.text('TOTAL PIEZAS', 170, y + 5);
      } else {
        doc.text('FOLIO', 16, y + 5);
        doc.text('FECHA', 36, y + 5);
        doc.text('CENTRO COSTOS', 56, y + 5);
        doc.text('ORDEN COMPRA (PO)', 88, y + 5);
        doc.text('ESTATUS GR', 120, y + 5);
        doc.text('PIEZAS', 150, y + 5);
        doc.text('VALOR MXN', 170, y + 5);
      }
      y += 7.5;

      pageFoliosCount = 0;
      pagePieces = 0;
      pageMXN = 0;
    }
  });

  // Draw Page Subtotal for final page
  if (pageFoliosCount > 0) {
    if (y > 225) {
      doc.addPage();
      drawCorporateHeader(
        doc,
        hidePrices ? 'REPORTE OPERATIVO Y AUDITORÍA DE ÓRDENES DE COMPRA (PO)' : 'REPORTE FINANCIERO Y AUDITORÍA DE ÓRDENES DE COMPRA (PO)',
        hidePrices ? `CONTROL DE ENTREGAS Y PIEZAS • ${poNumber || 'PO-ANUAL'}` : `SISTEMA FINANCIERO DE CONTROL • ${poNumber || 'PO-ANUAL'}`,
        `CLIENTE: ${clientName.substring(0, 15)}`,
        [188, 17, 0],
        logoBase64
      );
      y = 38;
    }

    doc.setFillColor(241, 245, 249);
    doc.setDrawColor(203, 213, 225);
    doc.rect(14, y, 188, 6.5, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.8);
    doc.setTextColor(15, 23, 42);
    doc.text(`SUBTOTAL PÁGINA (${pageFoliosCount} FOLIOS)`, 16, y + 4.5);
    if (hidePrices) {
      doc.text(`${pagePieces.toLocaleString()} PZAS`, 170, y + 4.5);
    } else {
      doc.text(`${pagePieces.toLocaleString()} PZAS`, 150, y + 4.5);
      doc.text(`$${pageMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 170, y + 4.5);
    }
    y += 8.5;
  }

  // TOTAL GENERAL AUDITADO DEL DOCUMENTO
  if (y > 230) {
    doc.addPage();
    drawCorporateHeader(
      doc,
      hidePrices ? 'REPORTE OPERATIVO Y AUDITORÍA DE ÓRDENES DE COMPRA (PO)' : 'REPORTE FINANCIERO Y AUDITORÍA DE ÓRDENES DE COMPRA (PO)',
      hidePrices ? `CONTROL DE ENTREGAS Y PIEZAS • ${poNumber || 'PO-ANUAL'}` : `SISTEMA FINANCIERO DE CONTROL • ${poNumber || 'PO-ANUAL'}`,
      `CLIENTE: ${clientName.substring(0, 15)}`,
      [188, 17, 0],
      logoBase64
    );
    y = 38;
  }

  doc.setFillColor(15, 23, 42); // Slate-900
  doc.roundedRect(14, y, 188, 9, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255);
  doc.text(`TOTAL GENERAL DEL DOCUMENTO (${globalFoliosCount} FOLIOS REGISTRADOS)`, 16, y + 5.5);
  if (hidePrices) {
    doc.setTextColor(52, 211, 153);
    doc.text(`${globalPieces.toLocaleString()} PIEZAS ENTREGADAS`, 150, y + 5.5);
  } else {
    doc.text(`${globalPieces.toLocaleString()} PZAS`, 146, y + 5.5);
    doc.setTextColor(52, 211, 153);
    doc.text(`$${globalMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 168, y + 5.5);
  }
  y += 12;

  // Stamp corporate footer cleanly at the bottom margin of ALL pages
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawCorporateFooter(doc, hidePrices ? 'Auditoría de Control Operativo PO SNF' : 'Auditoría de Control Financiero PO SNF', false);
    
    // Page numbering at bottom-right
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(148, 163, 184);
    doc.text(`Pág. ${p} de ${totalPages}`, 186, (doc.internal.pageSize.getHeight() || 279.4) - 17.5);
  }

  doc.save(`Reporte_PO_Historial_${clientName.replace(/\s+/g, '_')}.pdf`);
}

/**
 * Generates and downloads the official PO Closure, Encapsulation & Reconciliation Audit PDF Report.
 */
export async function downloadPoClosureReportPDF(params: {
  poRecord: PoRecord;
  clientName: string;
  folios?: Folio[];
  hidePrices?: boolean;
}) {
  const { poRecord, clientName, folios = [], hidePrices = false } = params;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const logoBase64 = await getCorporateLogoBase64();
  const isClosed = poRecord.status === 'CERRADA';
  const headerTitle = hidePrices
    ? (isClosed ? 'ACTA OFICIAL DE CONCILIACIÓN DE ORDEN DE COMPRA (PO)' : 'REPORTE OPERATIVO DE ORDEN DE COMPRA EN CURSO (PO)')
    : (isClosed ? 'ACTA OFICIAL DE CIERRE Y CONCILIACIÓN DE ORDEN DE COMPRA (PO)' : 'REPORTE FINANCIERO Y AUDITORÍA DE ORDEN DE COMPRA EN CURSO (PO)');
  
  const headerSubtitle = isClosed
    ? `EJERCICIO CONCILIADO • ${poRecord.poNumber}`
    : `CONTROL EN PROCESO • ${poRecord.poNumber}`;

  const accentColor: [number, number, number] = isClosed ? [15, 23, 42] : [188, 17, 0];

  drawCorporateHeader(
    doc,
    headerTitle,
    headerSubtitle,
    `CLIENTE: ${clientName.substring(0, 15)}`,
    accentColor,
    logoBase64
  );

  let y = 38;

  // 1. PO Encapsulation Summary Header Card
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.4);
  doc.roundedRect(14, y, 188, 38, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(15, 23, 42);
  doc.text('DATOS DE LA ORDEN DE COMPRA Y CONCILIACIÓN', 18, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(71, 85, 105);

  doc.text(`N° Orden de Compra (PO):`, 18, y + 13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(`${poRecord.poNumber}`, 62, y + 13);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text(`Estatus del Registro:`, 18, y + 19);
  doc.setFont('helvetica', 'bold');
  if (isClosed) {
    doc.setTextColor(220, 38, 38); // Red-600
    doc.text(`CERRADA Y CONCILIADA (FOLIOS ENCAPSULADOS)`, 62, y + 19);
  } else {
    doc.setTextColor(5, 150, 105); // Emerald-600
    doc.text(`ABIERTA / EN CURSO`, 62, y + 19);
  }

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text(`Fecha de Apertura:`, 18, y + 25);
  doc.text(`${(poRecord.createdAt || '').substring(0, 10)}`, 62, y + 25);

  if (isClosed && poRecord.closedAt) {
    doc.text(`Fecha de Cierre:`, 18, y + 31);
    doc.text(`${(poRecord.closedAt || '').substring(0, 10)} (Por: ${poRecord.closedBy || 'Controller Financiero'})`, 62, y + 31);
  } else {
    doc.text(`Última Actualización:`, 18, y + 31);
    doc.text(`${(poRecord.lastUpdated || '').substring(0, 10)}`, 62, y + 31);
  }

  // Right column of box 1
  const totalEncapsulatedFolios = poRecord.linkedFolioIds ? poRecord.linkedFolioIds.length : (poRecord.encapsulatedFolios ? poRecord.encapsulatedFolios.length : 0);
  doc.text(`Folios Encapsulados:`, 115, y + 13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(`${totalEncapsulatedFolios} folio(s)`, 155, y + 13);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text(`Piezas Totales:`, 115, y + 19);
  doc.text(`${poRecord.totalEncapsulatedPieces || 'N/A'} pzas`, 155, y + 19);

  if (poRecord.carriedToNextPoNumber) {
    doc.text(`Excedente Traspasado a:`, 115, y + 25);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 150, 105);
    doc.text(`${poRecord.carriedToNextPoNumber} (+$${(poRecord.surplusCarriedMXN || poRecord.balanceMXN || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })})`, 150, y + 25);
  } else if (poRecord.surplusOriginPoNumber) {
    doc.text(`Origen Saldo Anterior:`, 115, y + 25);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 64, 175);
    doc.text(`PO Anterior: ${poRecord.surplusOriginPoNumber}`, 150, y + 25);
  }

  y += 42;

  // 2. Metrics Box
  if (hidePrices) {
    doc.setFillColor(240, 253, 244);
    doc.setDrawColor(167, 243, 208);
    doc.roundedRect(14, y, 188, 28, 2, 2, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(6, 95, 70);
    doc.text('RESUMEN OPERATIVO DE CONCILIACIÓN DE PIEZAS', 18, y + 6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(51, 65, 85);
    doc.text(`Total Folios Auditados: ${totalEncapsulatedFolios} folios`, 18, y + 13);
    doc.text(`Total Piezas Validadas: ${poRecord.totalEncapsulatedPieces || '0'} pzas`, 18, y + 19);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(4, 120, 87);
    doc.text(`ESTATUS OPERATIVO: ENTREGADO Y CERTIFICADO`, 110, y + 16);

    y += 32;
  } else {
    const isSaldoAFavor = poRecord.balanceMXN >= 0;
    doc.setFillColor(isSaldoAFavor ? 240 : 254, isSaldoAFavor ? 253 : 242, isSaldoAFavor ? 244 : 242);
    doc.setDrawColor(isSaldoAFavor ? 167 : 252, isSaldoAFavor ? 243 : 165, isSaldoAFavor ? 208 : 165);
    doc.roundedRect(14, y, 188, 34, 2, 2, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(isSaldoAFavor ? 6 : 153, isSaldoAFavor ? 95 : 27, isSaldoAFavor ? 70 : 27);
    doc.text('ESTADO DE CUENTA Y SALDO FINAL DE LA ORDEN DE COMPRA', 18, y + 6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(51, 65, 85);

    doc.text(`Presupuesto Base PO: $${poRecord.annualBudgetMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 18, y + 13);
    
    // Distinción de Saldo Anterior y PO de Origen
    const originLabel = poRecord.surplusOriginPoNumber ? ` (Transferido de PO: ${poRecord.surplusOriginPoNumber})` : '';
    doc.text(`Saldo a Favor Anterior${originLabel}: $${poRecord.previousBalanceMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 18, y + 19);
    doc.text(`Capacidad Total Neta: $${poRecord.totalNetBudgetMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 18, y + 25);

    doc.text(`Total Folios Facturados/Entregados: $${poRecord.totalDeliveredMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 110, y + 13);
    
    if (poRecord.manualAdjustmentsMXN) {
      doc.text(`Ajustes Manuales: $${poRecord.manualAdjustmentsMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 110, y + 19);
    }

    // Resulting Balance Highlight Box
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    if (isSaldoAFavor) {
      doc.setTextColor(4, 120, 87); // Emerald-700
      doc.text(`SALDO A FAVOR DISPONIBLE: +$${poRecord.balanceMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 110, y + 26);
    } else {
      doc.setTextColor(185, 28, 28); // Red-700
      doc.text(`SALDO PENDIENTE / SOBREGIRO: -$${Math.abs(poRecord.balanceMXN).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 110, y + 26);
    }

    y += 38;
  }

  // Box: Trazabilidad y Distinción explícita de PO Anterior y Nueva PO
  if (poRecord.surplusOriginPoNumber || poRecord.carriedToNextPoNumber) {
    doc.setFillColor(241, 245, 249);
    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(14, y, 188, 13, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(15, 23, 42);
    doc.text('DISTINCIÓN DE PO ANTERIOR Y NUEVA PO (TRAZABILIDAD DE EXCEDENTE):', 18, y + 4.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(51, 65, 85);
    if (poRecord.surplusOriginPoNumber) {
      doc.text(`PO Anterior: ${poRecord.surplusOriginPoNumber} (Saldo transferido: +$${(poRecord.previousBalanceMXN || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN)  -->  Nueva PO: ${poRecord.poNumber} (Presupuesto Base: $${poRecord.annualBudgetMXN.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN)`, 18, y + 9.5);
    } else if (poRecord.carriedToNextPoNumber) {
      doc.text(`PO Anterior (Conciliada): ${poRecord.poNumber}  -->  Excedente Resultante: +$${(poRecord.surplusCarriedMXN || poRecord.balanceMXN || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN traspasado a Nueva PO: ${poRecord.carriedToNextPoNumber}`, 18, y + 9.5);
    }
    y += 16;
  }

  // Closure Notes if any
  if (poRecord.closureNotes || poRecord.notes) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    const noteStr = `Observaciones / Acta de Conciliación: ${poRecord.closureNotes || poRecord.notes}`;
    const splitNotes = doc.splitTextToSize(noteStr, 180);
    doc.text(splitNotes, 14, y);
    y += splitNotes.length * 3.5 + 2;
  }

  // 3. Encapsulated Folios Itemized Table Header
  doc.setFillColor(15, 23, 42);
  doc.rect(14, y, 188, 7.5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(255, 255, 255);
  if (hidePrices) {
    doc.text('FOLIO ENCAPSULADO', 17, y + 5);
    doc.text('FECHA', 50, y + 5);
    doc.text('CENTRO COSTOS / DESTINO', 80, y + 5);
    doc.text('ESTATUS ENTREGA', 140, y + 5);
    doc.text('CANTIDAD PIEZAS', 170, y + 5);
  } else {
    doc.text('FOLIO ENCAPSULADO', 17, y + 5);
    doc.text('FECHA', 50, y + 5);
    doc.text('CENTRO COSTOS / DESTINO', 75, y + 5);
    doc.text('ESTATUS GR', 125, y + 5);
    doc.text('PIEZAS', 150, y + 5);
    doc.text('VALOR MXN', 172, y + 5);
  }

  y += 7.5;

  // Prepare folios list from snapshot or live folios
  const foliosList: Array<{
    id: string;
    date: string;
    costCenter: string;
    grStatus: string;
    pieces: number;
    value: number;
    items?: Array<{ description: string; quantity: number; unitPriceMXN: number; totalMXN: number }>;
  }> = [];

  if (poRecord.encapsulatedFolios && poRecord.encapsulatedFolios.length > 0) {
    poRecord.encapsulatedFolios.forEach(snap => {
      foliosList.push({
        id: snap.folioId,
        date: (snap.shipmentDate || snap.createdDate || '').substring(0, 10),
        costCenter: snap.costCenterName || snap.destination || 'Sede Planta',
        grStatus: snap.grStatus || 'CONFIRMADO',
        pieces: snap.totalPieces || 0,
        value: snap.totalMXN || 0,
        items: snap.items
      });
    });
  } else if (poRecord.linkedFolioIds && poRecord.linkedFolioIds.length > 0) {
    poRecord.linkedFolioIds.forEach(fId => {
      const match = folios.find(f => f.id === fId);
      if (match) {
        const totalQty = match.items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
        const totalVal = match.items.reduce((s, i) => s + (Number(i.totalMXN) || 0), 0);
        foliosList.push({
          id: match.id,
          date: (match.shipmentDate || match.createdDate || '').substring(0, 10),
          costCenter: match.costCenterName || match.destination || 'Sede Planta',
          grStatus: match.grStatus || 'PENDIENTE',
          pieces: totalQty,
          value: totalVal,
          items: match.items.map(i => ({
            description: i.description,
            quantity: Number(i.quantity) || 0,
            unitPriceMXN: Number(i.unitPriceMXN) || 0,
            totalMXN: Number(i.totalMXN) || 0
          }))
        });
      } else {
        foliosList.push({
          id: fId,
          date: (poRecord.createdAt || '').substring(0, 10),
          costCenter: 'Folio Registrado en PO',
          grStatus: 'CONFIRMADO',
          pieces: 0,
          value: 0
        });
      }
    });
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);

  if (foliosList.length === 0) {
    doc.setTextColor(148, 163, 184);
    doc.text('No se encontraron folios vinculados en este registro de OC.', 18, y + 6);
    y += 10;
  } else {
    foliosList.forEach((item, idx) => {
      if (idx % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(14, y, 188, 6.5, 'F');
      }

      const isGr = item.grStatus === 'CONFIRMADO';
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(String(item.id), 17, y + 4.5);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(item.date, 50, y + 4.5);

      if (hidePrices) {
        doc.text(item.costCenter.substring(0, 30), 80, y + 4.5);
        if (isGr) {
          doc.setTextColor(5, 150, 105);
          doc.text('ENTREGADO', 140, y + 4.5);
        } else {
          doc.setTextColor(217, 119, 6);
          doc.text('POR ENTREGAR', 140, y + 4.5);
        }
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);
        doc.text(`${item.pieces} PZAS`, 170, y + 4.5);
      } else {
        doc.text(item.costCenter.substring(0, 26), 75, y + 4.5);
        if (isGr) {
          doc.setTextColor(5, 150, 105);
          doc.text('GR FACTURADO', 125, y + 4.5);
        } else {
          doc.setTextColor(217, 119, 6);
          doc.text('GR POR FACTURAR', 125, y + 4.5);
        }
        doc.setTextColor(51, 65, 85);
        doc.text(String(item.pieces), 150, y + 4.5);
        doc.setFont('helvetica', 'bold');
        doc.text(`$${item.value.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 172, y + 4.5);
      }

      y += 6.5;

      // Multi-page handling
      if (y > 235 && idx < foliosList.length - 1) {
        drawCorporateFooter(doc, poRecord.closedBy || 'Auditoría Controller SNF', false);
        doc.addPage();
        drawCorporateHeader(
          doc,
          headerTitle,
          headerSubtitle,
          `CLIENTE: ${clientName.substring(0, 15)}`,
          accentColor
        );
        y = 38;

        doc.setFillColor(15, 23, 42);
        doc.rect(14, y, 188, 7.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(255, 255, 255);
        if (hidePrices) {
          doc.text('FOLIO ENCAPSULADO', 17, y + 5);
          doc.text('FECHA', 50, y + 5);
          doc.text('CENTRO COSTOS / DESTINO', 80, y + 5);
          doc.text('ESTATUS ENTREGA', 140, y + 5);
          doc.text('CANTIDAD PIEZAS', 170, y + 5);
        } else {
          doc.text('FOLIO ENCAPSULADO', 17, y + 5);
          doc.text('FECHA', 50, y + 5);
          doc.text('CENTRO COSTOS / DESTINO', 75, y + 5);
          doc.text('ESTATUS GR', 125, y + 5);
          doc.text('PIEZAS', 150, y + 5);
          doc.text('VALOR MXN', 172, y + 5);
        }
        y += 7.5;
      }
    });
  }

  // Summary Row at end of table
  doc.setFillColor(241, 245, 249);
  doc.rect(14, y, 188, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(15, 23, 42);
  doc.text(`TOTAL ENCAPSULADO (${foliosList.length} Folios)`, 17, y + 5);
  const sumPieces = foliosList.reduce((s, f) => s + f.pieces, 0);
  const sumVal = foliosList.reduce((s, f) => s + f.value, 0);
  if (hidePrices) {
    doc.text(`${sumPieces.toLocaleString()} PIEZAS`, 165, y + 5);
  } else {
    doc.text(String(sumPieces), 150, y + 5);
    doc.text(`$${sumVal.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`, 172, y + 5);
  }
  y += 10;

  drawCorporateFooter(doc, poRecord.closedBy || 'Dirección de Finanzas SNF', true);
  const cleanName = `Reporte_OC_${poRecord.poNumber.replace(/\s+/g, '_')}_${isClosed ? 'CERRADA' : 'ACTIVA'}.pdf`;
  doc.save(cleanName);
}

export interface ReworkIncidentForPDF {
  id: string;
  folioId: string;
  clientId: string;
  clientName: string;
  costCenterId: string;
  costCenterName: string;
  type: 'RETRABAJO' | 'DEVOLUCION' | 'SCRAP' | string;
  originStage: 'INSPECCION' | 'ENTREGA_PLANTA' | 'SCRAP_ITEM_DANADO' | string;
  causeCategory: string;
  causeDetail: string;
  affectedPieces: number;
  reworkPiecesName?: string; // Nombre / Descripción de las piezas a retrabajar
  costEstimateMXN: number;
  responsibleDept: string;
  priority: string;
  status: string;
  reportedBy: string;
  dateReported: string;
  actionPlan?: string;
  resolutionNotes?: string;
  photoUrl?: string;
  photos?: string[];
  folioDetails?: {
    description?: string;
    totalPieces?: number;
    destination?: string;
    status?: string;
    clientCode?: string;
  };
}

/**
 * Generates and downloads the official PDF report for a Rework or Return Incident with professional layout, margins, typography, and up to 7 evidence images.
 */
export async function downloadReworkIncidentPDF(incident: ReworkIncidentForPDF) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const logoBase64 = await getCorporateLogoBase64();
  const isRetrabajo = incident.type === 'RETRABAJO';
  const isScrap = incident.type === 'SCRAP' || incident.originStage === 'SCRAP_ITEM_DANADO';
  const typeLabel = isScrap 
    ? 'REPORTE DE SCRAP / ITEM DAÑADO' 
    : isRetrabajo 
    ? 'REPORTE DE RETRABAJO / REPROCESO TÉCNICO' 
    : 'REPORTE DE DEVOLUCIÓN / RECHAZO EN PLANTA';
  const badgeText = `${incident.folioId} (${incident.id})`;

  drawCorporateHeader(
    doc,
    typeLabel,
    'SISTEMA DE CONTROL DE CALIDAD Y TRAZABILIDAD TÉCNICA SNF',
    badgeText,
    isScrap ? [217, 119, 6] : isRetrabajo ? [225, 29, 72] : [239, 68, 68],
    logoBase64
  );

  let y = 38;

  // Box 1: DATOS GENERALES Y DETALLE DEL FOLIO
  doc.setFillColor(248, 250, 252); // Slate-50
  doc.setDrawColor(203, 213, 225);
  doc.roundedRect(14, y, 188, 40, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(15, 23, 42);
  doc.text('DATOS GENERALES Y DETALLE DEL FOLIO', 18, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(51, 65, 85);

  doc.text(`Cliente: ${incident.clientName}`, 18, y + 13);
  doc.text(`Centro de Costos / Planta: ${incident.costCenterName}`, 110, y + 13);

  doc.text(`Folio ID: #${incident.folioId}`, 18, y + 19);
  doc.text(`Estatus Folio: ${incident.folioDetails?.status || incident.status}`, 110, y + 19);

  doc.text(`Fase de Origen: ${incident.originStage === 'INSPECCION' ? 'Fase 4: Inspección Control Calidad' : 'Fase 6: Entrega a Planta Cliente'}`, 18, y + 25);
  doc.text(`Fecha de Reporte: ${new Date(incident.dateReported).toLocaleString('es-MX')}`, 110, y + 25);

  doc.text(`Piezas Totales en Folio: ${incident.folioDetails?.totalPieces || 'N/A'} pzas`, 18, y + 31);
  doc.text(`Destino / Ciudad: ${incident.folioDetails?.destination || 'N/A'}`, 110, y + 31);

  doc.text(`Reportado Por: ${incident.reportedBy}`, 18, y + 37);
  doc.text(`Depto Responsable: ${incident.responsibleDept} | Prioridad: ${incident.priority}`, 110, y + 37);

  y += 44;

  // Box 2: DETALLE DEL RETRABAJO (PIEZAS Y CANTIDAD A RETRABAJAR)
  doc.setFillColor(254, 242, 242); // Rose-50
  doc.setDrawColor(252, 165, 165); // Red-300
  doc.roundedRect(14, y, 188, 36, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(153, 27, 27); // Red-800
  doc.text('DETALLE DEL DEFECTO Y PIEZAS A RETRABAJAR', 18, y + 6);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(185, 28, 28);
  doc.text(`Piezas / Componentes a Retrabajar: ${incident.reworkPiecesName || 'Ver detalle de la falla'}`, 18, y + 13);
  doc.text(`Cantidad a Retrabajar: ${incident.affectedPieces} pzas`, 110, y + 13);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(51, 65, 85);
  doc.text(`Categoría de Causa: ${incident.causeCategory}`, 18, y + 19);
  doc.text(`Prioridad / Severidad: ${incident.priority || 'MEDIA'}`, 110, y + 19);

  const detailLines = doc.splitTextToSize(`Descripción de la Falla: ${incident.causeDetail}`, 180);
  doc.text(detailLines, 18, y + 25);

  y += 40;

  // Box 3: PLAN DE ACCIÓN CORRECIVO Y NOTAS
  doc.setFillColor(240, 253, 244); // Emerald-50
  doc.setDrawColor(167, 243, 208); // Emerald-200
  doc.roundedRect(14, y, 188, 26, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(6, 95, 70); // Emerald-800
  doc.text('PLAN DE ACCIÓN TÉCNICO Y NOTAS DE CORRECCIÓN', 18, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(51, 65, 85);
  const planLines = doc.splitTextToSize(`Plan de Acción: ${incident.actionPlan || 'Acondicionamiento mecánico residual, lijado o re-decapado en tina química.'}`, 180);
  doc.text(planLines, 18, y + 12);

  if (incident.resolutionNotes) {
    const resLines = doc.splitTextToSize(`Notas de Reinspección: ${incident.resolutionNotes}`, 180);
    doc.text(resLines, 18, y + 20);
  }

  y += 30;

  // Box 4: EVIDENCIA FOTOGRÁFICA (HASTA 7 IMÁGENES)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text('EVIDENCIA FOTOGRÁFICA DE CAMPO (HASTA 7 IMÁGENES)', 14, y);
  y += 5;

  const photoList = incident.photos && incident.photos.length > 0 
    ? incident.photos.filter(p => !!p)
    : (incident.photoUrl ? [incident.photoUrl] : []);

  if (photoList.length > 0) {
    // If not enough room on Page 1, check if we need a new page for images
    if (y + 45 > 250) {
      doc.addPage();
      drawCorporateHeader(
        doc,
        `${typeLabel} - EVIDENCIA FOTOGRÁFICA`,
        'SISTEMA DE CONTROL DE CALIDAD Y TRACEABILIDAD TÉCNICA SNF',
        badgeText,
        isRetrabajo ? [225, 29, 72] : [239, 68, 68]
      );
      y = 38;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      doc.text('EVIDENCIA FOTOGRÁFICA REGISTRADA (HASTA 7 IMÁGENES)', 14, y);
      y += 5;
    }

    const cardWidth = 58;
    const cardHeight = 36;
    const startX = 14;
    const gapX = 7;
    const gapY = 5;

    const photoSlotLabels = [
      'Foto 1: Vista General Folio',
      'Foto 2: Acercamiento Defecto',
      'Foto 3: Micraje / Superficie',
      'Foto 4: Marcado / Serie',
      'Foto 5: Área de Pieza',
      'Foto 6: Proceso Retrabajo',
      'Foto 7: Inspección Final'
    ];

    photoList.slice(0, 7).forEach((photoData, idx) => {
      const col = idx % 3;
      const row = Math.floor(idx / 3);

      const px = startX + col * (cardWidth + gapX);
      const py = y + row * (cardHeight + gapY);

      // Card Box
      doc.setDrawColor(203, 213, 225);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(px, py, cardWidth, cardHeight, 1.5, 1.5, 'FD');

      try {
        if (typeof photoData === 'string' && photoData.startsWith('data:image')) {
          const format = photoData.includes('image/png') ? 'PNG' : 'JPEG';
          doc.addImage(photoData, format, px + 1, py + 1, cardWidth - 2, cardHeight - 6);
        } else if (typeof photoData === 'string' && photoData.startsWith('http')) {
          doc.addImage(photoData, 'JPEG', px + 1, py + 1, cardWidth - 2, cardHeight - 6);
        }
      } catch (err) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(6);
        doc.setTextColor(148, 163, 184);
        doc.text(`[Evidencia #${idx + 1} Registrada]`, px + 8, py + 16);
      }

      // Label
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6);
      doc.setTextColor(51, 65, 85);
      doc.text(photoSlotLabels[idx] || `Foto ${idx + 1}: Evidencia Campo`, px + 2, py + cardHeight - 1.5);
    });

    const totalRows = Math.ceil(Math.min(photoList.length, 7) / 3);
    y += totalRows * (cardHeight + gapY) + 4;
  } else {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(148, 163, 184);
    doc.text('Sin fotografías adjuntas registradas en el reporte.', 14, y + 4);
    y += 10;
  }

  drawCorporateFooter(doc, incident.reportedBy, true);

  const cleanFileName = `Reporte_Retrabajo_${incident.folioId}_${incident.id}.pdf`.replace(/\s+/g, '_');
  doc.save(cleanFileName);
}

export interface StrategicReportData {
  clientName: string;
  costCenterName?: string;
  incidents: any[];
  totalFolios?: number;
  stats?: {
    total: number;
    totalRetrabajos: number;
    totalDevoluciones: number;
    fromInspeccion: number;
    fromEntrega: number;
    totalPieces: number;
    pendingCount: number;
    inProgressCount?: number;
    resolvedCount: number;
    discardedCount: number;
  };
  chartByOriginAndType?: { fase: string; Retrabajos: number; Devoluciones: number }[];
  chartByCause?: { name: string; count: number }[];
  chartByStatusPie?: { name: string; value: number; color?: string }[];
  dateRange?: string;
  generatedBy: string;
  isUserClient?: boolean;
}

/**
 * Generates and downloads a complete Executive Strategic Report PDF
 * with integrated charts, KPI matrix, status badges, and strict client devolution filtering.
 */
export async function downloadReworksAndReturnsStrategicReportPDF(data: StrategicReportData) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const isClient = !!data.isUserClient;

  // Strict filtering: If it's a client report, ONLY include DEVOLUCIONES
  const rawIncidents = Array.isArray(data.incidents) ? data.incidents : [];
  const incidents = isClient
    ? rawIncidents.filter(i => i.type === 'DEVOLUCION' || i.originStage === 'ENTREGA_PLANTA')
    : rawIncidents;

  // Recalculate metrics from actual incidents list to ensure 100% data consistency
  const total = incidents.length;
  const retrabajos = incidents.filter(i => i.type === 'RETRABAJO');
  const devoluciones = incidents.filter(i => i.type === 'DEVOLUCION');
  const fromInspeccion = incidents.filter(i => i.originStage === 'INSPECCION');
  const fromEntrega = incidents.filter(i => i.originStage === 'ENTREGA_PLANTA');
  const resolved = incidents.filter(i => i.status === 'RESUELTO' || i.status === 'REINSPECCIONADO');
  const inProgress = incidents.filter(i => i.status === 'EN_REPROCESO');
  const pending = incidents.filter(i => i.status === 'PENDIENTE');
  const discarded = incidents.filter(i => i.status === 'DESECHADO');
  const totalPieces = incidents.reduce((acc, curr) => acc + (Number(curr.affectedPieces) || 0), 0);
  const resolutionRate = total > 0 ? Math.round((resolved.length / total) * 100) : 100;

  const logoBase64 = await getCorporateLogoBase64();
  const title = isClient
    ? 'INFORME ESTRATÉGICO DE DEVOLUCIONES EN PLANTA'
    : 'INFORME ESTRATÉGICO DE REPROCESOS Y DEVOLUCIONES';
  
  const clientLabel = data.clientName || (isClient ? 'CLIENTE' : 'CONSOLIDADO GENERAL');
  const badgeText = `${clientLabel} • ${new Date().toLocaleDateString('es-MX')}`;

  // =========================================================================
  // PAGE 1: TABLERO EJECUTIVO ESTRATÉGICO CON GRÁFICAS Y KPIs
  // =========================================================================
  drawCorporateHeader(
    doc,
    title,
    isClient ? 'CONTROL DE DEVOLUCIONES Y CALIDAD' : 'SISTEMA INTEGRAL DE CALIDAD Y TRAZABILIDAD SNF',
    badgeText,
    [188, 17, 0],
    logoBase64
  );

  let y = 37;

  // 1. Scope & Audit Parameters Box
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.roundedRect(14, y, 188, 24, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(15, 23, 42);
  doc.text('ALCANCE Y PARÁMETROS DEL INFORME AUDITADO', 18, y + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(51, 65, 85);
  doc.text(`Cliente / Empresa: ${clientLabel}`, 18, y + 10.5);
  doc.text(`Centro de Costos / Destino: ${data.costCenterName || 'Todas las Plantas'}`, 110, y + 10.5);

  const dateRangeStr = data.dateRange || 'Histórico Consolidado';
  doc.text(`Periodo Consultado: ${dateRangeStr}`, 18, y + 15.5);
  doc.text(`Fecha y Hora de Emisión: ${new Date().toLocaleString('es-MX')}`, 110, y + 15.5);

  doc.text(`Total Registros Auditados: ${total} ${isClient ? 'devoluciones' : 'casos'} (${totalPieces} piezas)`, 18, y + 20.5);
  doc.text(`Tipo de Reporte: ${isClient ? 'Devoluciones de Planta (Fase 6)' : 'Auditoría Integral (Fases 4 y 6)'}`, 110, y + 20.5);

  y += 27;

  // 2. Executive KPI Cards Matrix (3x2 Grid)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(15, 23, 42);
  doc.text(isClient ? 'MATRIZ DE INDICADORES DE DEVOLUCIONES EN PLANTA' : 'MATRIZ EJECUTIVA DE INDICADORES DE CALIDAD Y DESVIACIÓN', 14, y);
  y += 3.5;

  const cardW = 60;
  const cardH = 19;
  const gapX = 4;
  const gapY = 2.5;
  const startX = 14;

  const kpisToDraw = isClient
    ? [
        {
          title: 'TOTAL DEVOLUCIONES',
          value: `${total}`,
          subtext: 'Casos en entrega a planta',
          color: [225, 29, 72] as [number, number, number],
          bg: [255, 241, 242] as [number, number, number]
        },
        {
          title: 'PIEZAS DEVUELTAS',
          value: `${totalPieces} pcs`,
          subtext: 'Sujetas a acondicionamiento',
          color: [217, 119, 6] as [number, number, number],
          bg: [254, 243, 199] as [number, number, number]
        },
        {
          title: 'TASA DE RESOLUCIÓN',
          value: `${resolutionRate}%`,
          subtext: `${resolved.length} de ${total} resueltos`,
          color: [5, 150, 105] as [number, number, number],
          bg: [236, 253, 245] as [number, number, number]
        },
        {
          title: 'CASOS PENDIENTES',
          value: `${pending.length}`,
          subtext: 'En fila de atención técnica',
          color: [234, 88, 12] as [number, number, number],
          bg: [255, 247, 237] as [number, number, number]
        },
        {
          title: 'DEVOLUCIONES RESUELTAS',
          value: `${resolved.length}`,
          subtext: 'Inspeccionadas y validadas',
          color: [13, 148, 136] as [number, number, number],
          bg: [240, 253, 250] as [number, number, number]
        },
        {
          title: 'MATERIAL NO RECUPERABLE',
          value: `${discarded.length}`,
          subtext: 'Scrap / No apto para retrabajo',
          color: [71, 85, 105] as [number, number, number],
          bg: [241, 245, 249] as [number, number, number]
        }
      ]
    : [
        {
          title: 'TOTAL INCIDENCIAS',
          value: `${total}`,
          subtext: `${retrabajos.length} retrabajos | ${devoluciones.length} devoluciones`,
          color: [225, 29, 72] as [number, number, number],
          bg: [255, 241, 242] as [number, number, number]
        },
        {
          title: 'RETRABAJOS INTERNOS (F4)',
          value: `${retrabajos.length}`,
          subtext: `🔍 ${fromInspeccion.length} en Inspección CC`,
          color: [147, 51, 234] as [number, number, number],
          bg: [250, 245, 255] as [number, number, number]
        },
        {
          title: 'DEVOLUCIONES PLANTA (F6)',
          value: `${devoluciones.length}`,
          subtext: 'Rechazos en entrega final',
          color: [220, 38, 38] as [number, number, number],
          bg: [254, 242, 242] as [number, number, number]
        },
        {
          title: 'PIEZAS AFECTADAS',
          value: `${totalPieces} pcs`,
          subtext: 'Total piezas reprocesadas',
          color: [217, 119, 6] as [number, number, number],
          bg: [254, 243, 199] as [number, number, number]
        },
        {
          title: 'TASA DE RESOLUCIÓN',
          value: `${resolutionRate}%`,
          subtext: `${resolved.length} de ${total} resueltos con éxito`,
          color: [5, 150, 105] as [number, number, number],
          bg: [236, 253, 245] as [number, number, number]
        },
        {
          title: 'CASOS PENDIENTES',
          value: `${pending.length}`,
          subtext: `${inProgress.length} en reproceso actual`,
          color: [234, 88, 12] as [number, number, number],
          bg: [255, 247, 237] as [number, number, number]
        }
      ];

  kpisToDraw.forEach((kpi, idx) => {
    const col = idx % 3;
    const row = Math.floor(idx / 3);
    const kx = startX + col * (cardW + gapX);
    const ky = y + row * (cardH + gapY);

    doc.setFillColor(...kpi.bg);
    doc.setDrawColor(...kpi.color);
    doc.setLineWidth(0.35);
    doc.roundedRect(kx, ky, cardW, cardH, 1.5, 1.5, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.2);
    doc.setTextColor(...kpi.color);
    doc.text(kpi.title, kx + 3, ky + 4.5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text(kpi.value, kx + 3, ky + 11.5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(100, 116, 139);
    doc.text(kpi.subtext, kx + 3, ky + 16);
  });

  y += 2 * (cardH + gapY) + 4.5;

  // =========================================================================
  // 3. INTEGRATED 3 DASHBOARD VECTOR CHARTS WITH LABELS & BADGES
  // =========================================================================
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(15, 23, 42);
  doc.text('ANÁLISIS GRÁFICO INTEGRAL (REPRESENTACIÓN DIRECTA DEL TABLERO)', 14, y);
  y += 3.5;

  const chartBoxH = 54;
  const chart1W = 59;
  const chart2W = 63;
  const chart3W = 58;

  // -------------------------------------------------------------
  // CHART 1: Origen por Fase de Proceso (Inspección vs Entrega)
  // -------------------------------------------------------------
  const c1x = 14;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.roundedRect(c1x, y, chart1W, chartBoxH, 1.5, 1.5, 'FD');

  // Chart 1 Title Bar
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(c1x, y, chart1W, 7, 1.5, 1.5, 'F');
  doc.rect(c1x, y + 4, chart1W, 3, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.line(c1x, y + 7, c1x + chart1W, y + 7);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.setTextColor(15, 23, 42);
  doc.text(isClient ? 'DEVOLUCIONES POR ORIGEN' : 'ORIGEN POR FASE DE PROCESO', c1x + 3, y + 5);

  // Chart 1 Plot Area
  const plot1BaseY = y + 42;
  const maxValC1 = Math.max(retrabajos.length, devoluciones.length, fromInspeccion.length, fromEntrega.length, 5);

  // Grid lines
  doc.setDrawColor(241, 245, 249);
  doc.setLineWidth(0.2);
  doc.line(c1x + 6, plot1BaseY - 10, c1x + chart1W - 4, plot1BaseY - 10);
  doc.line(c1x + 6, plot1BaseY - 20, c1x + chart1W - 4, plot1BaseY - 20);
  doc.line(c1x + 6, plot1BaseY - 30, c1x + chart1W - 4, plot1BaseY - 30);

  // Baseline
  doc.setDrawColor(148, 163, 184);
  doc.setLineWidth(0.4);
  doc.line(c1x + 6, plot1BaseY, c1x + chart1W - 4, plot1BaseY);

  if (isClient) {
    // Client Chart 1: Entrega Planta Bar vs Resueltas
    const barW = 14;
    const h1 = Math.max(2, Math.min(30, (total / maxValC1) * 30));
    const h2 = Math.max(2, Math.min(30, (resolved.length / maxValC1) * 30));

    // Bar 1: Devoluciones
    doc.setFillColor(220, 38, 38);
    doc.rect(c1x + 12, plot1BaseY - h1, barW, h1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    doc.setTextColor(220, 38, 38);
    doc.text(`${total}`, c1x + 17, plot1BaseY - h1 - 1.5);

    // Bar 2: Resueltas
    doc.setFillColor(16, 185, 129);
    doc.rect(c1x + 33, plot1BaseY - h2, barW, h2, 'F');
    doc.setTextColor(16, 185, 129);
    doc.text(`${resolved.length}`, c1x + 38, plot1BaseY - h2 - 1.5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5);
    doc.setTextColor(71, 85, 105);
    doc.text('Entrega F6', c1x + 13, plot1BaseY + 3.5);
    doc.text('Resueltas', c1x + 35, plot1BaseY + 3.5);
  } else {
    // Internal Chart 1: Grouped Bars (Inspección F4 vs Entrega F6)
    const g1x = c1x + 11;
    const g2x = c1x + 35;
    const bW = 6.5;

    const inspRetrabajos = fromInspeccion.filter(i => i.type === 'RETRABAJO').length;
    const inspDevoluciones = fromInspeccion.filter(i => i.type === 'DEVOLUCION').length;
    const entrRetrabajos = fromEntrega.filter(i => i.type === 'RETRABAJO').length;
    const entrDevoluciones = fromEntrega.filter(i => i.type === 'DEVOLUCION').length;

    const hIR = Math.max(1.5, Math.min(30, (inspRetrabajos / maxValC1) * 30));
    const hID = Math.max(1.5, Math.min(30, (inspDevoluciones / maxValC1) * 30));
    const hER = Math.max(1.5, Math.min(30, (entrRetrabajos / maxValC1) * 30));
    const hED = Math.max(1.5, Math.min(30, (entrDevoluciones / maxValC1) * 30));

    // Group 1: Inspección F4
    doc.setFillColor(244, 63, 94); // Retrabajo
    doc.rect(g1x, plot1BaseY - hIR, bW, hIR, 'F');
    doc.setFillColor(239, 68, 68); // Devolución
    doc.rect(g1x + bW + 1, plot1BaseY - hID, bW, hID, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.setTextColor(244, 63, 94);
    doc.text(`${inspRetrabajos}`, g1x + 1.5, plot1BaseY - hIR - 1.2);
    doc.setTextColor(239, 68, 68);
    doc.text(`${inspDevoluciones}`, g1x + bW + 2.5, plot1BaseY - hID - 1.2);

    // Group 2: Entrega F6
    doc.setFillColor(244, 63, 94);
    doc.rect(g2x, plot1BaseY - hER, bW, hER, 'F');
    doc.setFillColor(239, 68, 68);
    doc.rect(g2x + bW + 1, plot1BaseY - hED, bW, hED, 'F');

    doc.setTextColor(244, 63, 94);
    doc.text(`${entrRetrabajos}`, g2x + 1.5, plot1BaseY - hER - 1.2);
    doc.setTextColor(239, 68, 68);
    doc.text(`${entrDevoluciones}`, g2x + bW + 2.5, plot1BaseY - hED - 1.2);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5);
    doc.setTextColor(71, 85, 105);
    doc.text('Inspección CC (F4)', g1x - 3, plot1BaseY + 3.5);
    doc.text('Entrega Planta (F6)', g2x - 3, plot1BaseY + 3.5);
  }

  // Legend Chart 1
  doc.setFillColor(244, 63, 94);
  doc.rect(c1x + 6, y + 49, 3, 2, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(4.8);
  doc.setTextColor(51, 65, 85);
  doc.text(isClient ? 'Devoluciones' : 'Retrabajos', c1x + 10, y + 50.5);

  doc.setFillColor(isClient ? 16 : 239, isClient ? 185 : 68, isClient ? 129 : 68);
  doc.rect(c1x + 32, y + 49, 3, 2, 'F');
  doc.text(isClient ? 'Resueltas' : 'Devoluciones', c1x + 36, y + 50.5);

  // -------------------------------------------------------------
  // CHART 2: Análisis de Causa Raíz (Top Defectos)
  // -------------------------------------------------------------
  const c2x = 77;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.roundedRect(c2x, y, chart2W, chartBoxH, 1.5, 1.5, 'FD');

  // Chart 2 Title Bar
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(c2x, y, chart2W, 7, 1.5, 1.5, 'F');
  doc.rect(c2x, y + 4, chart2W, 3, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.line(c2x, y + 7, c2x + chart2W, y + 7);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.setTextColor(15, 23, 42);
  doc.text('ANÁLISIS DE CAUSA RAÍZ (TOP DEFECTOS)', c2x + 3, y + 5);

  // Calculate Cause Distribution
  const causeCounts: Record<string, number> = {};
  incidents.forEach(inc => {
    const c = inc.causeCategory || inc.causeDetail || 'Otro Defecto';
    causeCounts[c] = (causeCounts[c] || 0) + 1;
  });

  const sortedCauses = Object.entries(causeCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const maxCauseVal = sortedCauses.length > 0 ? sortedCauses[0][1] : 1;
  let rowY = y + 11.5;

  if (sortedCauses.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6);
    doc.setTextColor(148, 163, 184);
    doc.text('Sin incidencias registradas en el periodo', c2x + 6, rowY + 12);
  } else {
    sortedCauses.forEach(([causeName, count]) => {
      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
      const barMaxWidth = 28;
      const curBarW = Math.max(2, (count / maxCauseVal) * barMaxWidth);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(5.2);
      doc.setTextColor(30, 41, 59);
      doc.text(causeName.replace(/_/g, ' ').substring(0, 18), c2x + 3, rowY + 3.2);

      // Track Bar
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(c2x + 23, rowY, barMaxWidth, 4, 0.8, 0.8, 'F');

      // Filled Bar
      doc.setFillColor(245, 158, 11);
      doc.roundedRect(c2x + 23, rowY, curBarW, 4, 0.8, 0.8, 'F');

      // Badge Label
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(5);
      doc.setTextColor(180, 83, 9);
      doc.text(`${count} (${pct}%)`, c2x + 52.5, rowY + 3);

      rowY += 8.2;
    });
  }

  // -------------------------------------------------------------
  // CHART 3: Estado de Resolución (% Avances)
  // -------------------------------------------------------------
  const c3x = 144;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.roundedRect(c3x, y, chart3W, chartBoxH, 1.5, 1.5, 'FD');

  // Chart 3 Title Bar
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(c3x, y, chart3W, 7, 1.5, 1.5, 'F');
  doc.rect(c3x, y + 4, chart3W, 3, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.line(c3x, y + 7, c3x + chart3W, y + 7);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.setTextColor(15, 23, 42);
  doc.text('ESTADO DE RESOLUCIÓN (% AVANCE)', c3x + 3, y + 5);

  // Status Breakdown List
  const statusItems = [
    { label: 'Resuelto', count: resolved.length, color: [16, 185, 129] as [number, number, number], bg: [236, 253, 245] as [number, number, number] },
    { label: 'Pendiente', count: pending.length, color: [245, 158, 11] as [number, number, number], bg: [255, 251, 235] as [number, number, number] },
    { label: 'En Reproceso', count: inProgress.length, color: [99, 102, 241] as [number, number, number], bg: [238, 242, 255] as [number, number, number] },
    { label: 'Desechado', count: discarded.length, color: [100, 116, 139] as [number, number, number], bg: [241, 245, 249] as [number, number, number] },
  ];

  let stY = y + 11.5;
  statusItems.forEach(item => {
    const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
    const progressW = 22;
    const curW = total > 0 ? Math.max(1, (item.count / total) * progressW) : 0;

    doc.setFillColor(...item.bg);
    doc.roundedRect(c3x + 3, stY - 1, chart3W - 6, 8, 1, 1, 'F');

    // Dot indicator
    doc.setFillColor(...item.color);
    doc.circle(c3x + 6, stY + 3, 1.3, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.setTextColor(30, 41, 59);
    doc.text(item.label, c3x + 9, stY + 3.3);

    // Progress bar inside item
    doc.setFillColor(226, 232, 240);
    doc.roundedRect(c3x + 25, stY + 1.2, progressW, 3.2, 0.6, 0.6, 'F');

    if (curW > 0) {
      doc.setFillColor(...item.color);
      doc.roundedRect(c3x + 25, stY + 1.2, curW, 3.2, 0.6, 0.6, 'F');
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.setTextColor(...item.color);
    doc.text(`${item.count} (${pct}%)`, c3x + 48.5, stY + 3.3);

    stY += 9.5;
  });

  y += chartBoxH + 4.5;

  // =========================================================================
  // 4. Strategic Executive Recommendations
  // =========================================================================
  doc.setFillColor(254, 242, 242);
  doc.setDrawColor(252, 165, 165);
  doc.setLineWidth(0.35);
  doc.roundedRect(14, y, 188, 26, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(153, 27, 27);
  doc.text(isClient ? 'DIRECTRICES Y ACCIONES DE ATENCIÓN A DEVOLUCIONES' : 'CONCLUSIONES ESTRATÉGICAS Y ACCIONES DIRECTIVAS RECOMENDADAS', 18, y + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(51, 65, 85);
  if (isClient) {
    doc.text('1. Atención Prioritaria en Planta: Todos los lotes devueltos cuentan con código de rastreo para acondicionamiento técnico ágil.', 18, y + 10.5);
    doc.text('2. Criterios de Aceptación Homologados: Verificación conjunta entre inspectores receptores y personal de calidad SNF.', 18, y + 15.5);
    doc.text('3. Trazabilidad Digital Garantizada: Consulta en línea del estatus de resolución de cada devolución con evidencias fotográficas.', 18, y + 20.5);
  } else {
    doc.text('1. Auditoría Preventiva en Decapado: Fortalecer control de temperaturas y concentraciones químicas para mitigar pintura residual.', 18, y + 10.5);
    doc.text('2. Criterios de Aceptación Alineados: Asegurar calibración continua entre inspectores de calidad SNF y personal receptor de planta.', 18, y + 15.5);
    doc.text('3. Trazabilidad Digital Integral: Mantener la bitácora fotográfica de 7 evidencias para validación inmediata y respaldo legal/financiero.', 18, y + 20.5);
  }

  // Footer on Page 1
  drawCorporateFooter(doc, data.generatedBy, false);

  // =========================================================================
  // PAGE 2: DETAILED INCIDENTS LOG TABLE WITH BADGES
  // =========================================================================
  doc.addPage();
  drawCorporateHeader(
    doc,
    `${title} - BITÁCORA DETALLADA`,
    isClient ? 'REGISTRO AUDITADO DE DEVOLUCIONES' : 'SISTEMA INTEGRAL DE CALIDAD SNF',
    badgeText,
    [188, 17, 0],
    logoBase64
  );

  let py2 = 38;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(isClient ? 'REGISTRO CONSOLIDADO DE DEVOLUCIONES EN PLANTA' : 'REGISTRO CONSOLIDADO DE INCIDENCIAS AUDITADAS', 14, py2);
  py2 += 4.5;

  // Table Header
  doc.setFillColor(15, 23, 42);
  doc.rect(14, py2, 188, 7, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.setTextColor(255, 255, 255);
  doc.text('FOLIO ID', 16, py2 + 4.5);
  doc.text('FECHA', 36, py2 + 4.5);
  doc.text(isClient ? 'ORIGEN' : 'TIPO / FASE', 56, py2 + 4.5);
  doc.text('CAUSA / DEFECTO', 86, py2 + 4.5);
  doc.text('PIEZAS', 128, py2 + 4.5);
  doc.text('PLAN DE ACCIÓN', 144, py2 + 4.5);
  doc.text('ESTATUS', 178, py2 + 4.5);

  py2 += 7;

  const incidentsList = incidents.slice(0, 18);
  if (incidentsList.length === 0) {
    doc.setFillColor(248, 250, 252);
    doc.rect(14, py2, 188, 12, 'F');
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(148, 163, 184);
    doc.text(isClient ? 'No hay devoluciones registradas en el criterio actual.' : 'No hay incidencias registradas en el criterio actual.', 65, py2 + 7.5);
    py2 += 14;
  } else {
    incidentsList.forEach((inc, idx) => {
      const isEven = idx % 2 === 0;
      doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 252);
      doc.rect(14, py2, 188, 8, 'F');

      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.2);
      doc.line(14, py2 + 8, 202, py2 + 8);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(15, 23, 42);
      doc.text(String(inc.folioId || inc.id).substring(0, 10), 16, py2 + 5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(71, 85, 105);
      const dateStr = inc.dateReported ? new Date(inc.dateReported).toLocaleDateString('es-MX') : 'N/A';
      doc.text(dateStr, 36, py2 + 5);

      const typeTxt = isClient
        ? '🚚 ENTREGA F6'
        : inc.type === 'RETRABAJO'
        ? '🔄 RETRABAJO'
        : '📦 DEVOLUCIÓN';
      doc.text(typeTxt, 56, py2 + 5);

      const causeTxt = (inc.causeDetail || inc.causeCategory || 'Defecto técnico').replace(/_/g, ' ').substring(0, 24);
      doc.text(causeTxt, 86, py2 + 5);

      doc.setFont('helvetica', 'bold');
      doc.text(`${inc.affectedPieces || 1} pcs`, 128, py2 + 5);

      doc.setFont('helvetica', 'normal');
      const actionTxt = (inc.actionPlan || 'Acondicionamiento técnico').substring(0, 20);
      doc.text(actionTxt, 144, py2 + 5);

      // Status Pill
      let statusColor: [number, number, number] = [234, 88, 12];
      if (inc.status === 'RESUELTO' || inc.status === 'REINSPECCIONADO') statusColor = [5, 150, 105];
      if (inc.status === 'EN_REPROCESO') statusColor = [99, 102, 241];
      if (inc.status === 'DESECHADO') statusColor = [100, 116, 139];

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...statusColor);
      doc.text(String(inc.status || 'PENDIENTE').substring(0, 12), 178, py2 + 5);

      py2 += 8;
    });
  }

  // Draw Signatures on Page 2 Footer
  drawCorporateFooter(doc, data.generatedBy, true);

  const cleanFileName = `Reporte_${isClient ? 'Devoluciones' : 'Reprocesos_Devoluciones'}_${clientLabel.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(cleanFileName);
}

export interface KpiPeriodRowForPDF {
  periodLabel: string;
  foliosCount: number;
  piecesReceived: number;
  piecesDelivered: number;
  reworkPieces: number; // Represents devoluciones
  devolucionPieces?: number;
  efficiencyPercent: number;
  compliance24hPercent?: number;
  compliant24hCount?: number;
  deliveredFoliosCount?: number;
  avgDeliveryHours?: number;
  status: string;
}

export interface KpiDeliveryDetailRowForPDF {
  folioId: string;
  folioNumber?: string;
  clientName?: string;
  costCenterName?: string;
  pieces: number;
  partDescription?: string;
  recoleccionDateFormatted: string;
  entregaDateFormatted: string;
  elapsedHours: number;
  is24hCompliant: boolean;
  status: string;
}

export interface KpisReportData {
  clientName?: string;
  costCenterName?: string;
  dateRange: string;
  periodMode: 'DIAS' | 'SEMANAS' | 'MESES';
  generatedBy: string;
  summary: {
    totalPiecesReceived: number;
    totalPiecesDelivered: number;
    totalReworkReturnPieces: number; // Represents total devoluciones
    efficiencyPercent: number;
    totalFoliosCount: number;
    compliance24hPercent?: number;
    totalCompliant24h?: number;
    totalDeliveredFolios?: number;
    avgDeliveryHours?: number;
  };
  periods: KpiPeriodRowForPDF[];
  deliveries?: KpiDeliveryDetailRowForPDF[];
}

/**
 * Generates an executive, highly detailed, professional PDF report for KPIs and quality efficiency,
 * including an integrated dynamic visual chart with pieces bars and yellow efficiency line.
 */
export async function downloadKpisPerformanceReportPDF(data: KpisReportData) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const pageWidth = 216;
  const pageHeight = 279.4;
  const logoBase64 = await getCorporateLogoBase64();

  const clientLabel = data.clientName || 'CONSOLIDADO DE CLIENTE';
  const costCenterLabel = data.costCenterName || 'TODAS LAS PLANTAS / CEDIS';
  const badgeText = `${clientLabel.substring(0, 26)} • REPORTE DE EFICIENCIA`;

  // Draw Corporate Header on Page 1
  drawCorporateHeader(
    doc,
    'INFORME EJECUTIVO DE KPIS Y EFICIENCIA OPERATIVA',
    'AUDITORÍA DE VOLUMEN ENTREGADO, RETRABAJOS Y % DE EFICIENCIA',
    badgeText,
    [188, 17, 0], // Brand Red
    logoBase64
  );

  let y = 37;

  // 1. Audit & Filter Scope Parameters Card
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.roundedRect(14, y, 188, 22, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(15, 23, 42);
  doc.text('PARÁMETROS Y ALCANCE DE AUDITORÍA OPERATIVA DE CALIDAD', 18, y + 4.8);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(51, 65, 85);
  doc.text(`Cliente / Socio Comercial: ${clientLabel}`, 18, y + 10);
  doc.text(`Centro de Costos / Planta: ${costCenterLabel}`, 110, y + 10);

  const modeStr = data.periodMode === 'DIAS' ? 'Vista por Días' : data.periodMode === 'SEMANAS' ? 'Vista por Semanas' : 'Vista por Meses';
  doc.text(`Periodo Consultado: ${data.dateRange || 'Histórico'} (${modeStr})`, 18, y + 15);
  doc.text(`Emisión: ${new Date().toLocaleString('es-MX')}`, 110, y + 15);

  doc.text(`Cálculo de Eficiencia: Cantidad de Piezas contra Retrabajos Cliente = % Eficiencia`, 18, y + 19.5);
  doc.text(`Auditor / Responsable: ${data.generatedBy || 'Sistema Central SIC PRO'}`, 110, y + 19.5);

  y += 25;

  // 2. Executive Metric Cards Matrix (6 Cards: 2 rows of 3)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(15, 23, 42);
  doc.text('RESUMEN EJECUTIVO DE INDICADORES CLAVE DE DESEMPEÑO (KPIS)', 14, y);
  y += 3.5;

  const cardW = 60;
  const cardH = 16;
  const gapX = 4;
  const gapY = 2.5;
  const startX = 14;

  const compliance24Val = data.summary.compliance24hPercent ?? 100;
  const is24hOk = compliance24Val >= 95;

  const kpisList = [
    {
      title: 'PIEZAS ENTREGADAS',
      value: `${data.summary.totalPiecesDelivered.toLocaleString()} pcs`,
      subtext: `En ${data.summary.totalFoliosCount} folios procesados`,
      color: [30, 41, 59] as [number, number, number],
      bg: [241, 245, 249] as [number, number, number]
    },
    {
      title: 'RETRABAJOS CLIENTE',
      value: `${data.summary.totalReworkReturnPieces.toLocaleString()} pcs`,
      subtext: `Piezas en retrabajo de cliente`,
      color: [239, 68, 68] as [number, number, number],
      bg: [254, 242, 242] as [number, number, number]
    },
    {
      title: 'TASA DE EFICIENCIA (%)',
      value: `${data.summary.efficiencyPercent.toFixed(1)}%`,
      subtext: `Piezas conformes vs retrabajos`,
      color: [202, 138, 4] as [number, number, number], // Amber/Yellow
      bg: [254, 252, 232] as [number, number, number]
    },
    {
      title: 'CUMPLIMIENTO 24H (META 95%)',
      value: `${compliance24Val.toFixed(1)}%`,
      subtext: `${data.summary.totalCompliant24h ?? 0} de ${data.summary.totalDeliveredFolios ?? 0} en ≤ 24 hrs`,
      color: (is24hOk ? [16, 185, 129] : [217, 119, 6]) as [number, number, number],
      bg: (is24hOk ? [240, 253, 244] : [255, 251, 235]) as [number, number, number]
    },
    {
      title: 'TIEMPO PROMEDIO TRÁNSITO',
      value: `${(data.summary.avgDeliveryHours ?? 0).toFixed(1)} hrs`,
      subtext: `Recolección hasta planta`,
      color: [8, 145, 178] as [number, number, number],
      bg: [236, 254, 255] as [number, number, number]
    },
    {
      title: 'TOTAL FOLIOS AUDITADOS',
      value: `${data.summary.totalFoliosCount}`,
      subtext: `Lotes inspeccionados en periodo`,
      color: [124, 58, 237] as [number, number, number],
      bg: [245, 243, 255] as [number, number, number]
    }
  ];

  kpisList.forEach((card, idx) => {
    const col = idx % 3;
    const row = Math.floor(idx / 3);
    const cx = startX + col * (cardW + gapX);
    const cy = y + row * (cardH + gapY);

    doc.setFillColor(card.bg[0], card.bg[1], card.bg[2]);
    doc.setDrawColor(card.color[0], card.color[1], card.color[2]);
    doc.setLineWidth(0.35);
    doc.roundedRect(cx, cy, cardW, cardH, 1.5, 1.5, 'FD');

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.2);
    doc.setTextColor(card.color[0], card.color[1], card.color[2]);
    doc.text(card.title, cx + 3, cy + 4.2);

    // Value
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(card.value, cx + 3, cy + 10);

    // Subtext
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(71, 85, 105);
    doc.text(card.subtext, cx + 3, cy + 13.8);
  });

  y += (cardH * 2) + (gapY * 2) + 4;

  // 3. INTEGRATED DYNAMIC VISUAL CHART IN PDF
  const chartBoxW = 188;
  const chartBoxH = 46;
  doc.setFillColor(250, 250, 250);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.35);
  doc.roundedRect(14, y, chartBoxW, chartBoxH, 2, 2, 'FD');

  // Chart Title & Subtitle
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(15, 23, 42);
  doc.text('REPRESENTACIÓN GRÁFICA: CANTIDAD DE PIEZAS VS. % DE EFICIENCIA', 18, y + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.8);
  doc.setTextColor(100, 116, 139);
  doc.text('Barras azules: Cantidad de piezas entregadas | Línea amarilla: Porcentaje de eficiencia operativa', 18, y + 8.5);

  // Legend (Top-Right)
  // Blue bar legend
  doc.setFillColor(59, 130, 246);
  doc.rect(100, y + 3.8, 3, 3, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(5.5);
  doc.setTextColor(51, 65, 85);
  doc.text('Pzas Entregadas', 104, y + 6.2);

  // Yellow line legend
  doc.setDrawColor(202, 138, 4);
  doc.setLineWidth(0.8);
  doc.line(133, y + 5.3, 138, y + 5.3);
  doc.setFillColor(250, 204, 21);
  doc.circle(135.5, y + 5.3, 0.9, 'FD');
  doc.setTextColor(161, 98, 7);
  doc.text('% Eficiencia', 140, y + 6.2);

  // Green target line legend (Meta 95%)
  doc.setDrawColor(34, 197, 94);
  doc.setLineWidth(0.8);
  doc.line(164, y + 5.3, 170, y + 5.3);
  doc.setTextColor(22, 163, 74);
  doc.text('Meta (95%)', 172, y + 6.2);

  // Chart plotting area coordinates
  const plotLeft = 28;
  const plotRight = 190;
  const plotTop = y + 13;
  const plotBottom = y + 38;
  const plotWidth = plotRight - plotLeft;
  const plotHeight = plotBottom - plotTop;

  // Find max pieces for scaling
  const maxPiecesInPeriods = Math.max(...data.periods.map(p => p.piecesDelivered), 10);
  const chartMaxPieces = Math.ceil(maxPiecesInPeriods / 50) * 50 || 100;

  // Draw light horizontal grid lines (3 lines: 0%, 50%, 100%)
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.15);
  [0, 0.5, 1].forEach(ratio => {
    const gy = plotBottom - (ratio * plotHeight);
    doc.line(plotLeft, gy, plotRight, gy);

    // Left Y Axis labels (Pieces)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5);
    doc.setTextColor(100, 116, 139);
    const pieceVal = Math.round(ratio * chartMaxPieces);
    doc.text(String(pieceVal), plotLeft - 2, gy + 1.2, { align: 'right' });

    // Right Y Axis labels (Percentage)
    doc.setTextColor(161, 98, 7);
    doc.text(`${Math.round(ratio * 100)}%`, plotRight + 2, gy + 1.2, { align: 'left' });
  });

  // Fixed 95% Green Compliance Reference Line
  const meta95Ratio = 0.95;
  const meta95Y = plotBottom - (meta95Ratio * plotHeight);
  doc.setDrawColor(34, 197, 94); // Green 500
  doc.setLineWidth(0.5);
  let dashedX = plotLeft;
  while (dashedX < plotRight) {
    doc.line(dashedX, meta95Y, Math.min(dashedX + 2.5, plotRight), meta95Y);
    dashedX += 4;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(4.8);
  doc.setTextColor(22, 163, 74);
  doc.text('95%', plotRight + 2, meta95Y + 1.2, { align: 'left' });

  // Plot Periods (Bars + Yellow Line)
  const displayPeriods = data.periods.slice(0, 12);
  const numPeriods = displayPeriods.length;

  if (numPeriods > 0) {
    const colStep = plotWidth / numPeriods;
    const barWidth = Math.min(12, Math.max(5, colStep * 0.45));

    // Array to store yellow line points
    const efficiencyPoints: { x: number; y: number; val: number }[] = [];

    displayPeriods.forEach((p, idx) => {
      const centerX = plotLeft + (idx * colStep) + (colStep / 2);

      // 1. Draw Blue Bar for Pieces
      const barHeight = Math.max(1, (p.piecesDelivered / chartMaxPieces) * plotHeight);
      const barY = plotBottom - barHeight;
      const barX = centerX - (barWidth / 2);

      doc.setFillColor(59, 130, 246); // Blue
      doc.setDrawColor(37, 99, 235);
      doc.setLineWidth(0.2);
      doc.roundedRect(barX, barY, barWidth, barHeight, 0.8, 0.8, 'FD');

      // Pieces value label on top of bar
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(4.8);
      doc.setTextColor(30, 41, 59);
      doc.text(p.piecesDelivered.toLocaleString(), centerX, Math.max(plotTop - 0.5, barY - 1), { align: 'center' });

      // Period X Axis Label (bottom)
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5);
      doc.setTextColor(71, 85, 105);
      const shortLabel = p.periodLabel.length > 10 ? p.periodLabel.substring(0, 9) + '.' : p.periodLabel;
      doc.text(shortLabel, centerX, plotBottom + 4.5, { align: 'center' });

      // 2. Compute Efficiency Y Position (0 to 100%)
      const effRatio = Math.min(1, Math.max(0, p.efficiencyPercent / 100));
      const effY = plotBottom - (effRatio * plotHeight);
      efficiencyPoints.push({ x: centerX, y: effY, val: p.efficiencyPercent });
    });

    // 3. Draw Yellow Efficiency Connecting Line
    if (efficiencyPoints.length > 1) {
      doc.setDrawColor(202, 138, 4); // Warm Golden Yellow
      doc.setLineWidth(0.7);
      for (let i = 0; i < efficiencyPoints.length - 1; i++) {
        doc.line(efficiencyPoints[i].x, efficiencyPoints[i].y, efficiencyPoints[i + 1].x, efficiencyPoints[i + 1].y);
      }
    }

    // 4. Draw Yellow Nodes with Efficiency Percentage Labels
    efficiencyPoints.forEach(pt => {
      doc.setFillColor(250, 204, 21); // Bright yellow
      doc.setDrawColor(161, 98, 7);
      doc.setLineWidth(0.35);
      doc.circle(pt.x, pt.y, 1.2, 'FD');

      // Value text in amber
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(5);
      doc.setTextColor(161, 98, 7);
      doc.text(`${pt.val.toFixed(1)}%`, pt.x, Math.max(plotTop + 1, pt.y - 2), { align: 'center' });
    });
  }

  y += chartBoxH + 5;

  // 4. Data Breakdown Table
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(15, 23, 42);
  doc.text('DESGLOSE OPERATIVO DETALLADO POR PERIODO', 14, y);
  y += 3.5;

  const tableColWidths = [42, 20, 26, 26, 26, 26, 22]; // Total = 188mm
  const tableHeaders = ['PERIODO', 'FOLIOS', 'RECIBIDAS', 'ENTREGADAS', 'RETRABAJOS', '% EFICIENCIA', 'ESTATUS'];

  // Table Header Row
  doc.setFillColor(15, 23, 42); // Slate-900
  doc.roundedRect(14, y, 188, 6.5, 1, 1, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.2);
  doc.setTextColor(255, 255, 255);

  let hx = 14;
  tableHeaders.forEach((th, i) => {
    const align = i === 0 ? 'left' : 'center';
    const textX = align === 'left' ? hx + 2 : hx + (tableColWidths[i] / 2);
    doc.text(th, textX, y + 4.2, { align });
    hx += tableColWidths[i];
  });

  y += 6.5;

  // Table Data Rows
  const rowHeight = 5.8;

  data.periods.forEach((row, rIdx) => {
    // Check if we need to spill over to Page 2
    if (y > pageHeight - 45) {
      doc.addPage();
      drawCorporateHeader(
        doc,
        'INFORME EJECUTIVO DE KPIS Y EFICIENCIA OPERATIVA (CONTINUACIÓN)',
        'DESGLOSE DE PERIODOS Y AUDITORÍA DE CALIDAD',
        badgeText,
        [188, 17, 0],
        logoBase64
      );

      y = 38;

      // Repeat Table Header on next page
      doc.setFillColor(15, 23, 42);
      doc.roundedRect(14, y, 188, 6.5, 1, 1, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.2);
      doc.setTextColor(255, 255, 255);

      let nhx = 14;
      tableHeaders.forEach((th, i) => {
        const align = i === 0 ? 'left' : 'center';
        const textX = align === 'left' ? nhx + 2 : nhx + (tableColWidths[i] / 2);
        doc.text(th, textX, y + 4.2, { align });
        nhx += tableColWidths[i];
      });

      y += 6.5;
    }

    // Zebra row background
    if (rIdx % 2 === 1) {
      doc.setFillColor(248, 250, 252);
      doc.rect(14, y, 188, rowHeight, 'F');
    }

    // Row bottom border
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.15);
    doc.line(14, y + rowHeight, 202, y + rowHeight);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(15, 23, 42);

    let rx = 14;
    
    // 0. Period Label
    doc.setFont('helvetica', 'bold');
    doc.text(row.periodLabel.substring(0, 24), rx + 2, y + 3.8);
    rx += tableColWidths[0];

    // 1. Folios Count
    doc.setFont('helvetica', 'normal');
    doc.text(String(row.foliosCount), rx + (tableColWidths[1] / 2), y + 3.8, { align: 'center' });
    rx += tableColWidths[1];

    // 2. Pieces Received
    doc.text(row.piecesReceived.toLocaleString(), rx + (tableColWidths[2] / 2), y + 3.8, { align: 'center' });
    rx += tableColWidths[2];

    // 3. Pieces Delivered
    doc.setFont('helvetica', 'bold');
    doc.text(row.piecesDelivered.toLocaleString(), rx + (tableColWidths[3] / 2), y + 3.8, { align: 'center' });
    rx += tableColWidths[3];

    // 4. Devoluciones
    const devolucionesCount = row.devolucionPieces !== undefined ? row.devolucionPieces : row.reworkPieces;
    doc.setTextColor(devolucionesCount > 0 ? 225 : 100, devolucionesCount > 0 ? 29 : 116, devolucionesCount > 0 ? 72 : 139);
    doc.text(devolucionesCount.toLocaleString(), rx + (tableColWidths[4] / 2), y + 3.8, { align: 'center' });
    rx += tableColWidths[4];

    // 5. % Eficiencia
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(
      row.efficiencyPercent >= 98 ? 5 : row.efficiencyPercent >= 93 ? 14 : row.efficiencyPercent >= 85 ? 202 : 225,
      row.efficiencyPercent >= 98 ? 150 : row.efficiencyPercent >= 93 ? 165 : row.efficiencyPercent >= 85 ? 138 : 29,
      row.efficiencyPercent >= 98 ? 105 : row.efficiencyPercent >= 93 ? 233 : row.efficiencyPercent >= 85 ? 4 : 72
    );
    doc.text(`${row.efficiencyPercent.toFixed(1)}%`, rx + (tableColWidths[5] / 2), y + 3.8, { align: 'center' });
    rx += tableColWidths[5];

    // 6. Estatus
    doc.setFontSize(5.5);
    doc.setTextColor(
      row.status === 'EXCELENTE' ? 5 : row.status === 'OPTIMO' ? 14 : row.status === 'EN_RIESGO' ? 217 : 225,
      row.status === 'EXCELENTE' ? 150 : row.status === 'OPTIMO' ? 165 : row.status === 'EN_RIESGO' ? 119 : 29,
      row.status === 'EXCELENTE' ? 105 : row.status === 'OPTIMO' ? 233 : row.status === 'EN_RIESGO' ? 6 : 72
    );
    doc.text(row.status, rx + (tableColWidths[6] / 2), y + 3.8, { align: 'center' });

    y += rowHeight;
  });

  // Table Totals / Summary Row
  doc.setFillColor(226, 232, 240); // Slate-200
  doc.rect(14, y, 188, rowHeight + 0.5, 'F');
  doc.setDrawColor(15, 23, 42);
  doc.setLineWidth(0.3);
  doc.line(14, y + rowHeight + 0.5, 202, y + rowHeight + 0.5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.2);
  doc.setTextColor(15, 23, 42);

  let tx = 14;
  doc.text('TOTAL CONSOLIDADO', tx + 2, y + 4.2);
  tx += tableColWidths[0];

  doc.text(String(data.summary.totalFoliosCount), tx + (tableColWidths[1] / 2), y + 4.2, { align: 'center' });
  tx += tableColWidths[1];

  doc.text(data.summary.totalPiecesReceived.toLocaleString(), tx + (tableColWidths[2] / 2), y + 4.2, { align: 'center' });
  tx += tableColWidths[2];

  doc.text(data.summary.totalPiecesDelivered.toLocaleString(), tx + (tableColWidths[3] / 2), y + 4.2, { align: 'center' });
  tx += tableColWidths[3];

  doc.setTextColor(data.summary.totalReworkReturnPieces > 0 ? 225 : 15, data.summary.totalReworkReturnPieces > 0 ? 29 : 23, data.summary.totalReworkReturnPieces > 0 ? 72 : 42);
  doc.text(data.summary.totalReworkReturnPieces.toLocaleString(), tx + (tableColWidths[4] / 2), y + 4.2, { align: 'center' });
  tx += tableColWidths[4];

  doc.setTextColor(202, 138, 4); // Yellow
  doc.text(`${data.summary.efficiencyPercent.toFixed(1)}%`, tx + (tableColWidths[5] / 2), y + 4.2, { align: 'center' });
  tx += tableColWidths[5];

  doc.setTextColor(15, 23, 42);
  doc.text('GLOBAL', tx + (tableColWidths[6] / 2), y + 4.2, { align: 'center' });

  y += rowHeight + 4;

  // 5. DETALLE DE ENTREGAS Y TIEMPOS DE TRÁNSITO (RECOLECCIÓN VS. ENTREGA EN PLANTA)
  // Initiates on a dedicated page to guarantee spacious, pristine presentation of every delivery record
  doc.addPage();
  drawCorporateHeader(
    doc,
    'INFORME DETALLADO DE ENTREGAS Y TIEMPOS DE TRÁNSITO',
    'AUDITORÍA DE RECOLECCIÓN VS. ENTREGA EN PLANTA (SLA ≤ 24 HORAS)',
    badgeText,
    [188, 17, 0],
    logoBase64
  );

  let dy = 37;

  // Delivery SLA Executive Summary Bar
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.roundedRect(14, dy, 188, 17, 2, 2, 'FD');

  const totalDelivered = data.summary.totalDeliveredFolios ?? (data.deliveries?.length || 0);
  const compliantCount = data.summary.totalCompliant24h ?? (data.deliveries?.filter(d => d.is24hCompliant).length || 0);
  const overdueCount = Math.max(0, totalDelivered - compliantCount);
  const compliancePct = totalDelivered > 0 ? (compliantCount / totalDelivered) * 100 : (data.summary.compliance24hPercent ?? 100);
  const avgHours = data.summary.avgDeliveryHours ?? 0;

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(15, 23, 42);
  doc.text('AUDITORÍA DE TIEMPOS DE TRÁNSITO Y CUMPLIMIENTO DEL SLA OPERATIVO (META 95%)', 18, dy + 4.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.2);
  doc.setTextColor(71, 85, 105);
  doc.text('Trazabilidad desde el registro de Recolección hasta la Entrega en Planta. Meta de cumplimiento: ≤ 24.0 horas.', 18, dy + 8.5);

  // 4 mini badges in summary bar
  const badgeY = dy + 13.0;
  // Chip 1: Total Folios Entregados
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.setTextColor(15, 23, 42);
  doc.text(`Entregas Auditadas: ${totalDelivered}`, 18, badgeY);

  // Chip 2: En Tiempo ≤ 24h
  doc.setTextColor(compliancePct >= 95 ? 22 : 180, compliancePct >= 95 ? 163 : 83, compliancePct >= 95 ? 74 : 9);
  doc.text(`En Tiempo (≤ 24h): ${compliantCount} (${compliancePct.toFixed(1)}%)`, 72, badgeY);

  // Chip 3: Fuera de Tiempo
  doc.setTextColor(overdueCount > 0 ? 225 : 100, overdueCount > 0 ? 29 : 116, overdueCount > 0 ? 72 : 139);
  doc.text(`Excedieron 24h: ${overdueCount}`, 126, badgeY);

  // Chip 4: Promedio
  doc.setTextColor(8, 145, 178);
  doc.text(`Promedio Tránsito: ${avgHours.toFixed(1)} hrs`, 165, badgeY);

  dy += 21;

  // Section Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(15, 23, 42);
  doc.text('REGISTRO INDIVIDUAL DE ENTREGAS: RECOLECCIÓN VS. ENTREGA EN PLANTA', 14, dy);
  dy += 3.5;

  // Delivery Table Headers
  // Widths: [Folio: 24, Planta/Destino: 36, Piezas: 16, Recolección: 36, Entrega Planta: 36, T. Transcurso: 20, SLA 24h: 20]
  // Total = 24 + 36 + 16 + 36 + 36 + 20 + 20 = 188 mm
  const dColWidths = [24, 36, 16, 36, 36, 20, 20];
  const dHeaders = ['FOLIO', 'PLANTA / DESTINO', 'PIEZAS', 'FECHA RECOLECCIÓN', 'ENTREGA EN PLANTA', 'T. TRANSCURSO', 'ESTATUS SLA'];

  const drawDeliveryTableHeader = (currY: number) => {
    doc.setFillColor(15, 23, 42);
    doc.roundedRect(14, currY, 188, 6.5, 1, 1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.8);
    doc.setTextColor(255, 255, 255);

    let hx = 14;
    dHeaders.forEach((th, i) => {
      const align = i === 1 ? 'left' : 'center';
      const textX = align === 'left' ? hx + 2 : hx + (dColWidths[i] / 2);
      doc.text(th, textX, currY + 4.2, { align });
      hx += dColWidths[i];
    });
  };

  drawDeliveryTableHeader(dy);
  dy += 6.5;

  const deliveriesList = data.deliveries || [];
  const dRowHeight = 5.8;

  if (deliveriesList.length === 0) {
    doc.setFillColor(248, 250, 252);
    doc.rect(14, dy, 188, 8, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text('No se encontraron registros de entregas en planta para los filtros seleccionados.', 108, dy + 5, { align: 'center' });
    dy += 8;
  } else {
    deliveriesList.forEach((row, rIdx) => {
      // Pagination check
      if (dy > pageHeight - 45) {
        doc.addPage();
        drawCorporateHeader(
          doc,
          'INFORME DETALLADO DE ENTREGAS Y TIEMPOS DE TRÁNSITO (CONTINUACIÓN)',
          'AUDITORÍA DE RECOLECCIÓN VS. ENTREGA EN PLANTA (SLA ≤ 24 HORAS)',
          badgeText,
          [188, 17, 0],
          logoBase64
        );
        dy = 38;
        drawDeliveryTableHeader(dy);
        dy += 6.5;
      }

      // Zebra striping
      if (rIdx % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(14, dy, 188, dRowHeight, 'F');
      }

      // Row divider
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.15);
      doc.line(14, dy + dRowHeight, 202, dy + dRowHeight);

      let rx = 14;

      // 0. Folio
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.2);
      doc.setTextColor(15, 23, 42);
      const folioStr = (row.folioNumber || row.folioId || '').substring(0, 15);
      doc.text(folioStr, rx + (dColWidths[0] / 2), dy + 3.8, { align: 'center' });
      rx += dColWidths[0];

      // 1. Planta / Destino
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.0);
      doc.setTextColor(51, 65, 85);
      const destinationStr = (row.costCenterName || row.clientName || 'Planta').substring(0, 24);
      doc.text(destinationStr, rx + 2, dy + 3.8);
      rx += dColWidths[1];

      // 2. Piezas
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(row.pieces.toLocaleString(), rx + (dColWidths[2] / 2), dy + 3.8, { align: 'center' });
      rx += dColWidths[2];

      // 3. Fecha Recolección
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5.8);
      doc.setTextColor(71, 85, 105);
      doc.text(row.recoleccionDateFormatted || '—', rx + (dColWidths[3] / 2), dy + 3.8, { align: 'center' });
      rx += dColWidths[3];

      // 4. Fecha Entrega Planta
      doc.text(row.entregaDateFormatted || '—', rx + (dColWidths[4] / 2), dy + 3.8, { align: 'center' });
      rx += dColWidths[4];

      // 5. Tiempo Transcurso (hrs)
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.2);
      doc.setTextColor(8, 145, 178); // Cyan-600
      const transitText = row.elapsedHours > 0 ? `${row.elapsedHours.toFixed(1)} hrs` : '—';
      doc.text(transitText, rx + (dColWidths[5] / 2), dy + 3.8, { align: 'center' });
      rx += dColWidths[5];

      // 6. Estatus SLA 24H (Badge)
      const isOk = row.is24hCompliant;
      const badgeW = 17;
      const badgeH = 4.2;
      const badgeX = rx + (dColWidths[6] - badgeW) / 2;
      const badgeY = dy + (dRowHeight - badgeH) / 2;

      if (isOk) {
        doc.setFillColor(220, 252, 231); // Green-100
        doc.setDrawColor(34, 197, 94);   // Green-500
        doc.setLineWidth(0.2);
        doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 1, 1, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(5.0);
        doc.setTextColor(22, 163, 74);   // Green-600
        doc.text('≤ 24h OK', badgeX + (badgeW / 2), badgeY + 3.0, { align: 'center' });
      } else {
        doc.setFillColor(254, 242, 242); // Red-50
        doc.setDrawColor(239, 68, 68);   // Red-500
        doc.setLineWidth(0.2);
        doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 1, 1, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(4.8);
        doc.setTextColor(220, 38, 38);   // Red-600
        doc.text('> 24h SLA', badgeX + (badgeW / 2), badgeY + 3.0, { align: 'center' });
      }

      dy += dRowHeight;
    });

    // Deliveries Totals Summary Row
    doc.setFillColor(226, 232, 240);
    doc.rect(14, dy, 188, dRowHeight + 0.5, 'F');
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(0.3);
    doc.line(14, dy + dRowHeight + 0.5, 202, dy + dRowHeight + 0.5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.0);
    doc.setTextColor(15, 23, 42);

    let dtx = 14;
    doc.text('TOTAL ENTREGAS', dtx + (dColWidths[0] / 2), dy + 4.0, { align: 'center' });
    dtx += dColWidths[0];

    doc.text(`${totalDelivered} folios auditados`, dtx + 2, dy + 4.0);
    dtx += dColWidths[1];

    const totalDeliveredPieces = deliveriesList.reduce((s, d) => s + (d.pieces || 0), 0);
    doc.text(totalDeliveredPieces.toLocaleString(), dtx + (dColWidths[2] / 2), dy + 4.0, { align: 'center' });
    dtx += dColWidths[2];

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.5);
    doc.setTextColor(71, 85, 105);
    doc.text('Ciclos Completos', dtx + (dColWidths[3] / 2), dy + 4.0, { align: 'center' });
    dtx += dColWidths[3];

    doc.text('Entregado en Destino', dtx + (dColWidths[4] / 2), dy + 4.0, { align: 'center' });
    dtx += dColWidths[4];

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.0);
    doc.setTextColor(8, 145, 178);
    doc.text(`${avgHours.toFixed(1)} hrs prom`, dtx + (dColWidths[5] / 2), dy + 4.0, { align: 'center' });
    dtx += dColWidths[5];

    doc.setTextColor(compliancePct >= 95 ? 22 : 180, compliancePct >= 95 ? 163 : 83, compliancePct >= 95 ? 74 : 9);
    doc.text(`${compliancePct.toFixed(1)}% CUMPLE`, dtx + (dColWidths[6] / 2), dy + 4.0, { align: 'center' });

    dy += dRowHeight + 4;
  }

  // Final check for Narrative & Signature block space
  if (dy > pageHeight - 52) {
    doc.addPage();
    drawCorporateHeader(
      doc,
      'AUDITORÍA DE CALIDAD Y AVAL INSTITUCIONAL',
      'DICTAMEN TÉCNICO DE CUMPLIMIENTO OPERATIVO Y FIRMAS',
      badgeText,
      [188, 17, 0],
      logoBase64
    );
    dy = 40;
  }

  // Quality & Logistics Evaluation & Conclusion Narrative Box
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.3);
  doc.roundedRect(14, dy, 188, 22, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(15, 23, 42);
  doc.text('EVALUACIÓN Y CONCLUSIÓN DE DESEMPEÑO OPERATIVO, LOGÍSTICA Y CALIDAD', 18, dy + 4.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.3);
  doc.setTextColor(71, 85, 105);
  const narrative = `Durante el periodo auditado (${data.dateRange}), se entregaron ${data.summary.totalPiecesDelivered.toLocaleString()} piezas terminadas correspondientes a ${data.summary.totalFoliosCount} folios auditados. Frente a ${data.summary.totalReworkReturnPieces.toLocaleString()} piezas registradas en devolución de cliente, la tasa de eficiencia global se situó en ${data.summary.efficiencyPercent.toFixed(1)}%. En cuanto al desempeño logístico puerta a puerta, el cumplimiento de entrega en planta dentro del estándar de 24 horas alcanzó un ${compliancePct.toFixed(1)}% (${compliantCount} de ${totalDelivered} folios a tiempo) con un tiempo promedio de transcurso de ${avgHours.toFixed(1)} horas. Este informe avala la trazabilidad y estricto apego al SLA contratado.`;
  const splitNarrative = doc.splitTextToSize(narrative, 180);
  doc.text(splitNarrative, 18, dy + 8.5);

  // Signatures on Footer
  drawCorporateFooter(doc, data.generatedBy, true);

  // Save the PDF
  const cleanName = `Reporte_KPIS_Eficiencia_${(clientLabel || 'Cliente').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(cleanName);
}




